import React, { useState, useRef, useEffect, useCallback, useMemo, type ReactNode } from 'react';

import { Menu, ActionIcon, Modal, useComputedColorScheme } from '@mantine/core';
import {
  IconTrash,
  IconPencil,
  IconClipboardText,
  IconDotsVertical,
  IconCheck,
  IconEdit,
} from '@tabler/icons-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
// Use global GeoJSON types to align with mapbox-gl typings
import '../styles/RaceInsightsPage.css';
import { useNavigate } from 'react-router-dom';

import ModalRaceInsights from '../components/modals/Modal';
import { ModalSaveTraining } from '../components/modals/ModalSaveTraining';
import { PerformanceLabelPanel } from '../components/real-polars/PerformanceLabelPanel';
import { MapControlIcon } from '../components/shared/MapControlIcon';
import { QuadrantModeIcon } from '../components/shared/QuadrantModeIcon';
import { type MapCameraState } from '../components/side-panels/MapPanel';
import SidePanelPage, {
  type SidePanelPageProperties,
} from '../components/side-panels/SidePanelPage';
import { SHOW_SEGMENT_LABELS } from '../config/featureVisibility';
import { type PanelId } from '../context/PanelState';
import { useProcessingOverlay } from '../context/ProcessingOverlayContext';
import { EnhancedSelect } from '../EnhancedSelect';
import { ReactComponent as ArrowRightIcon } from '../react-icons/arrow-right-icon.svg';
import { ReactComponent as MinusIcon } from '../react-icons/minusIcon.svg';
import { ReactComponent as PlusIcon } from '../react-icons/plusIcon.svg';
import { ReactComponent as SailTrackLogo } from '../react-icons/SailTrackLogo.svg';
import { ReactComponent as UploadFileIcon } from '../react-icons/uploadFile.svg';
import { showMessage } from '../showMessage';
import { getCurrentColorScheme } from '../theme';
import {
  type TrackPoint,
  type HighlightSegment,
  type Tack,
  type Event,
  type Boat,
  type EventFile,
  type InfoRaceItem,
  type Mark,
  type FetchTrackDataResponse,
  type Segment,
  type RenderizarRacesProperties,
  type PanelMode,
  type LayoutKey,
  type SidePanelPosition,
  type MetricBounds,
  type RelatedTrackFile,
  type OverlayTrackLayer,
  type SharingMetadata,
} from '../types';
import { distanciaPuntos } from '../utils/geo';
import { createHighlightId } from '../utils/highlight';
import { safeMoveLayer } from '../utils/mapBoxHelpers';
import { getCurrentMapColorMode, resolveMapColorScheme } from '../utils/mapboxStyle';
import { canMergeStyledLineFeatures, isFiniteMetricValue } from '../utils/mapLineStyle';
import { calcularEstadisticasTack } from '../utils/tack';
import { type TrackSelectionType } from '../utils/tenMinutePoints';
import { pollUploadJob } from '../utils/uploadJobPolling';

import type mapboxgl from 'mapbox-gl';

import 'mapbox-gl/dist/mapbox-gl.css';
// Utility to extract a safe API error message
const getApiMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    const m = (error.response?.data as { message?: unknown })?.message;
    if (typeof m === 'string') {
      return m;
    }
  }
  if (error && typeof (error as { message?: unknown }).message === 'string') {
    return (error as { message: string }).message;
  }
  return fallback;
};

// Avoid duplicating Mapbox paint property keys
const LINE_COLOR_PROP = 'line-color' as const;
const HIGHLIGHT_SOURCE_ID_PREFIX = 'highlight-lines-' as const;
const HIGHLIGHT_LAYER_ID_PREFIX = 'highlight-lines-layer-' as const;
const LEGACY_HIGHLIGHT_SOURCE_PREFIX = 'highlight-line-' as const;
const LEGACY_HIGHLIGHT_LAYER_PREFIX = 'highlight-line-layer-' as const;
const LINE_OPACITY_PROP = 'line-opacity' as const;
const VMGPC_COLOR_SCALE_MIN = 80;
const VMGPC_COLOR_SCALE_MAX = 100;
const RENAME_TRANSLATION_KEY = 'common.rename' as const;
const DARK_MAP_HIGHLIGHT_COLORS = [
  'rgba(218, 190, 81, 0.9)',
  'rgba(98, 178, 205, 0.9)',
  'rgba(96, 195, 145, 0.9)',
  'rgba(220, 111, 116, 0.9)',
  'rgba(160, 135, 215, 0.9)',
  'rgba(220, 143, 88, 0.9)',
] as const;

type DraftHighlight = HighlightSegment & { source: 'map' | 'chart' | 'chartPreview' };
type HighlightFeatureProperties = {
  id: string;
  color: string;
  opacity: number;
  kind: 'saved' | 'draft';
};
type HighlightFeature = GeoJSON.Feature<GeoJSON.LineString, HighlightFeatureProperties>;
type HighlightFeatureCollection = GeoJSON.FeatureCollection<
  GeoJSON.LineString,
  HighlightFeatureProperties
>;

const hasSameHighlightRange = (left: HighlightSegment, right: HighlightSegment): boolean => {
  if (left.endIndex == null || right.endIndex == null) {
    return false;
  }
  return (
    Math.min(left.startIndex, left.endIndex) === Math.min(right.startIndex, right.endIndex) &&
    Math.max(left.startIndex, left.endIndex) === Math.max(right.startIndex, right.endIndex)
  );
};

const makeHighlightFeature = (
  id: string,
  segment: HighlightSegment,
  data: TrackPoint[],
  kind: HighlightFeatureProperties['kind'],
): HighlightFeature | null => {
  if (data.length === 0 || segment.endIndex == null) {
    return null;
  }
  const from = Math.min(segment.startIndex, segment.endIndex);
  const to = Math.max(segment.startIndex, segment.endIndex);
  const coordinates = data
    .slice(from, to + 1)
    .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lon))
    .map((point) => [point.lon, point.lat] as GeoJSON.Position);

  if (coordinates.length < 2) {
    return null;
  }

  return {
    type: 'Feature',
    id,
    geometry: { type: 'LineString', coordinates },
    properties: {
      id,
      color: segment.color,
      opacity: kind === 'draft' ? 0.75 : 0.9,
      kind,
    },
  };
};

const buildHighlightFeatureCollection = (
  segments: HighlightSegment[],
  draftHighlight: DraftHighlight | null,
  data: TrackPoint[],
): HighlightFeatureCollection => {
  const features = segments
    .map((segment) => makeHighlightFeature(segment.id, segment, data, 'saved'))
    .filter((feature): feature is HighlightFeature => feature !== null);

  const draftMatchesSavedSegment = Boolean(
    draftHighlight &&
      draftHighlight.endIndex != null &&
      segments.some(
        (segment) =>
          segment.color === draftHighlight.color && hasSameHighlightRange(segment, draftHighlight),
      ),
  );
  if (
    draftHighlight &&
    draftHighlight.endIndex !== null &&
    draftHighlight.startIndex !== draftHighlight.endIndex &&
    !draftMatchesSavedSegment
  ) {
    const draftFeature = makeHighlightFeature(
      `draft-${draftHighlight.source}`,
      draftHighlight,
      data,
      'draft',
    );
    if (draftFeature) {
      features.push(draftFeature);
    }
  }

  return { type: 'FeatureCollection', features };
};

const formatDateYMD = (value: unknown): string | null => {
  if (typeof value === 'string' || typeof value === 'number' || value instanceof Date) {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
  }
  return null;
};

// Common i18n keys and fallbacks used multiple times
const NOTIF_ERROR = 'notifications.error' as const;
const KEY_PLACEHOLDER_EVENT = 'race.placeholders.event' as const;
const KEY_PLACEHOLDER_BOAT = 'race.placeholders.boat' as const;
const LBL_EVENT_ELLIPSIS = 'Event...' as const;
const LBL_BOAT_ELLIPSIS = 'Boat...' as const;
const KEY_REPORTS_GENERATE = 'reports.generate' as const;
const LBL_GENERATE_REPORT = 'Generate report' as const;
const RACE_NUMBER_KEY = 'race.raceNumber' as const;

const formatOverlayDateLabel = (iso?: string | null): string | null => {
  if (!iso) {
    return null;
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

const formatOverlayTimeRange = (start?: string | null, end?: string | null): string | null => {
  const opts: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };
  const startDate = start ? new Date(start) : null;
  const endDate = end ? new Date(end) : null;
  const startLabel =
    startDate && !Number.isNaN(startDate.getTime())
      ? startDate.toLocaleTimeString(undefined, opts)
      : null;
  const endLabel =
    endDate && !Number.isNaN(endDate.getTime())
      ? endDate.toLocaleTimeString(undefined, opts)
      : null;
  if (startLabel && endLabel) {
    return `${startLabel} – ${endLabel}`;
  }
  return startLabel ?? endLabel;
};

const buildSharingLabel = (
  sharing?: SharingMetadata | null,
  translate?: (key: string, defaultValue?: string) => string,
): string => {
  if (!sharing) {
    return '';
  }
  if (sharing.sharedByMe) {
    return translate ? translate('sharing.sharedByMe', 'Shared by me') : 'Shared by me';
  }
  if (sharing.sharedWithMe) {
    return translate ? translate('sharing.sharedWithMe', 'Shared with me') : 'Shared with me';
  }
  return translate ? translate('sharing.onlyMine', 'Only mine') : 'Only mine';
};

const isOwnedSharing = (sharing?: SharingMetadata | null): boolean => sharing?.isOwner ?? true;

// Shared menu item bits
const GENERATE_REPORT_LABEL = '' as const; // unused placeholder; labels provided via i18n below
const DEFAULT_API_ERROR_MSG = 'Request failed';
const GENERATE_REPORT_ICON = <IconClipboardText size={16} />;
const RELATED_TRACK_COLORS = [
  '#FF6B6B',
  '#4ECDC4',
  '#FFD166',
  '#6E67FF',
  '#2EC4B6',
  '#F28482',
] as const;

const OVERLAY_KEY_PREFIX_FULL = 'full';
const OVERLAY_KEY_PREFIX_SEGMENT = 'segment';

type OverlayKeyKind = 'full' | 'segment';
type ParsedOverlayKey =
  | { kind: 'full'; fileId: number }
  | { kind: 'segment'; fileId: number; segmentId: number };

const buildFullOverlayKey = (fileId: number): string => `${OVERLAY_KEY_PREFIX_FULL}:${fileId}`;
const buildSegmentOverlayKey = (fileId: number, segmentId: number): string =>
  `${OVERLAY_KEY_PREFIX_SEGMENT}:${fileId}:${segmentId}`;

const parseOverlayKey = (key: string): ParsedOverlayKey | null => {
  const [prefix, fileIdString, segmentIdString] = key.split(':');
  if (prefix === OVERLAY_KEY_PREFIX_FULL && fileIdString) {
    const fileId = Number.parseInt(fileIdString, 10);
    return Number.isFinite(fileId) ? { kind: 'full', fileId } : null;
  }
  if (prefix === OVERLAY_KEY_PREFIX_SEGMENT && fileIdString && segmentIdString) {
    const fileId = Number.parseInt(fileIdString, 10);
    const segmentId = Number.parseInt(segmentIdString, 10);
    return Number.isFinite(fileId) && Number.isFinite(segmentId)
      ? { kind: 'segment', fileId, segmentId }
      : null;
  }
  return null;
};

type PrimaryTimeRange = {
  startUtc: string;
  endUtc: string;
};

type TrackDataCacheEntry = {
  trackDataArray: TrackPoint[];
  loa: number;
  polarsTxt: string | null;
  polarsAngles: number[];
  polarsTws: number[];
  polarsSpeeds: number[][];
  polarsStds: number[][];
};

/**
 * Redimensiona el mapa solo cuando el estilo ya está cargado.
 * Vuelve a intentarlo hasta N veces con un retardo fijo.
 */
/**
 * Redimensiona el mapa solo cuando el estilo ya está cargado.
 * Espera al evento 'load' si es necesario.
 */
function safeResizeMap(map?: mapboxgl.Map | null): void {
  if (!map) {
    return;
  }

  // No intentes redimensionar si el contenedor está oculto
  const container = map.getContainer?.() as HTMLElement | undefined;
  if (!container || container.clientWidth === 0 || container.clientHeight === 0) {
    return;
  }

  // Si el estilo aún no está listo, espera al evento 'load'
  if (!map.isStyleLoaded?.()) {
    map.once('load', () => safeResizeMap(map));
    return;
  }

  // Estilo cargado → redimensiona
  try {
    map.resize();
  } catch {
    // silent
  }
}
/** Three-dots contextual menu using Mantine, with per-item color and icon tinting. */
function ThreeDotsMenu({
  items,
  ariaLabel,
}: {
  items: {
    label: string;
    onClick: () => void;
    icon?: ReactNode;
    color?: string;
    iconColor?: string;
  }[];
  ariaLabel?: string;
}) {
  const { t } = useTranslation('common');
  const colorScheme = useComputedColorScheme('dark', { getInitialValueInEffect: false });
  const label = ariaLabel ?? t('common.moreOptions', 'More options');
  const renderLeft = (icon: ReactNode | undefined, color: string) => {
    if (!icon) {
      return;
    }
    // If the icon is a ReactElement (e.g., from @tabler/icons-react), set its color and size
    if (React.isValidElement(icon)) {
      const typedIcon = icon as React.ReactElement<{ color?: string; size?: number }>;
      return React.cloneElement(typedIcon, {
        color,
        size: 16,
      });
    }
    // Fallback: wrap as-is; many Mantine icons inherit currentColor
    return icon;
  };

  return (
    <Menu withinPortal position="bottom-start" offset={4} shadow="sm" zIndex={10_000}>
      <Menu.Target>
        {/* Avoid bubbling to parent buttons and reserve width */}
        <div
          className="three-dots-target"
          role="button"
          tabIndex={0}
          aria-label={label}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.stopPropagation();
            }
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            cursor: 'pointer',
            transform: 'none',
            transition: 'none',
          }}
        >
          <ActionIcon
            variant="subtle"
            aria-label={label}
            size="sm"
            className="three-dots-action"
            style={{
              transform: 'none',
            }}
          >
            <IconDotsVertical size={16} />
          </ActionIcon>
        </div>
      </Menu.Target>
      <Menu.Dropdown className="three-dots-dropdown">
        {items.map((it, index) => {
          const defaultColor = colorScheme === 'light' ? '#12283a' : '#ffffff'; // dark blue in light mode, white otherwise
          const textColor = it.color ?? defaultColor;
          const iconColor = it.iconColor ?? textColor;
          const labelNode =
            typeof it.label === 'string' ? (
              <span style={{ color: textColor }}>{it.label}</span>
            ) : (
              it.label
            );
          return (
            <Menu.Item
              key={index}
              leftSection={renderLeft(it.icon, iconColor)}
              onClick={it.onClick}
              style={{ color: textColor }}
            >
              {labelNode}
            </Menu.Item>
          );
        })}
      </Menu.Dropdown>
    </Menu>
  );
}
const sanitize = (s: string) => s.trim().replace(',', '.');
const fetchMarksByRaceId = async (
  raceId: number,
): Promise<Array<{ id: number; lat: number; lon: number }>> => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.post(
      `${process.env.REACT_APP_API_BASE_URL ?? ''}/api/track/marks`,
      { raceId },
      { headers: { Authorization: `Bearer ${token ?? ''}` } },
    );
    if (
      response.data &&
      typeof response.data === 'object' &&
      'success' in response.data &&
      'data' in response.data
    ) {
      const responseData = response.data as {
        success: boolean;
        data: Array<{ id: number; lat: number; lon: number }>;
      };
      return responseData.success ? responseData.data : [];
    }
    return [];
  } catch (error) {
    console.error('Error fetching marks:', error);
    return [];
  }
};
function computeMetricBoundsIQR(data: number[]): {
  min: number;
  max: number;
  iqrMin: number;
  iqrMax: number;
} {
  if (data.length === 0) {
    return { min: 0, max: 0, iqrMin: 0, iqrMax: 0 };
  }

  const sorted = [...data].sort((a, b) => a - b);
  const min = sorted[0];
  // Use index access to ensure a definite number type (avoid number | undefined)
  const max = sorted.at(-1) ?? 0;

  const q1 = sorted[Math.floor(sorted.length * 0.25)];
  const q3 = sorted[Math.floor(sorted.length * 0.75)];
  const iqr = q3 - q1;

  // límites usando 3·IQR pero sin salirse de los absolutos
  const iqrMin = Math.max(min, q1 - 1.5 * iqr);
  const iqrMax = Math.min(max ?? 0, (q3 ?? 0) + 1.5 * iqr);

  return { min, max: max, iqrMin, iqrMax };
}
function obtenerRaces(
  firstIndex: number,
  lastIndex: number,
  fileId: number,
  eventFiles: EventFile[],
  segmentId?: number,
): Segment[] {
  const selectedFile = eventFiles.find((file) => file.id === fileId) || null;
  if (!selectedFile) {
    return [];
  }
  const segmentosFiltrados = selectedFile.segments
    .filter(
      (segment) =>
        segment.segment_type === true &&
        !(segment.end_index < firstIndex || segment.start_index > lastIndex),
    )
    .map((segment) => ({
      ...segment,
      id: segment.id,
      segment_type: segment.segment_type,
      start_index: Math.max(segment.start_index, firstIndex),
      end_index: Math.min(segment.end_index, lastIndex),
      name: segment.name,
    }));
  if (segmentId !== undefined) {
    const segmentoEncontrado = segmentosFiltrados.find((segment) => segment.id === segmentId);
    return segmentoEncontrado ? [segmentoEncontrado] : [];
  }
  return segmentosFiltrados;
}

const START_SEGMENT_HALF_WINDOW_POINTS = 180;
type RangeSelectionKind = 'leg' | 'start';
type IndexRange = { start: number; end: number };
type RangeOverride = IndexRange & { kind?: RangeSelectionKind };

const getFiniteTimeToGun = (point: TrackPoint | undefined): number | null => {
  const value = point?.tmtogun;
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

const findGunIndexForSegment = (
  segment: Segment,
  trackDataArray: TrackPoint[] | undefined,
): number | null => {
  const storedGunIndex =
    typeof segment.gun_index === 'number' && Number.isFinite(segment.gun_index)
      ? Math.round(segment.gun_index)
      : null;
  if (!trackDataArray || trackDataArray.length === 0) {
    return storedGunIndex;
  }
  if (
    storedGunIndex !== null &&
    storedGunIndex >= 0 &&
    storedGunIndex < trackDataArray.length
  ) {
    return storedGunIndex;
  }
  const start = Math.max(0, Math.min(trackDataArray.length - 1, segment.start_index));
  const end = Math.max(start, Math.min(trackDataArray.length - 1, segment.end_index));
  let previousValue: number | null = null;
  let closestToGun: { index: number; distance: number } | null = null;

  for (let index = start; index <= end; index += 1) {
    const value = getFiniteTimeToGun(trackDataArray[index]);
    if (value === null) {
      continue;
    }
    if (previousValue !== null && previousValue > 0 && value <= 0) {
      return index;
    }
    const distance = Math.abs(value);
    if (!closestToGun || distance < closestToGun.distance) {
      closestToGun = { index, distance };
    }
    previousValue = value;
  }

  return closestToGun?.index ?? null;
};

const buildStartRangeForSegment = (
  segment: Segment,
  trackDataArray?: TrackPoint[],
): IndexRange => {
  const anchorIndex = findGunIndexForSegment(segment, trackDataArray) ?? segment.start_index;
  return {
    start: anchorIndex - START_SEGMENT_HALF_WINDOW_POINTS,
    end: anchorIndex + START_SEGMENT_HALF_WINDOW_POINTS,
  };
};

const isSameIndexRange = (left: IndexRange | undefined, right: IndexRange | null): boolean =>
  Boolean(left && right && left.start === right.start && left.end === right.end);

const getFirstUpwindLegRangeForSegment = (
  segment: Segment | null | undefined,
  modes: EventFile['modes'] | undefined,
): IndexRange | null => {
  if (!segment || !modes || modes.length === 0) {
    return null;
  }
  const firstUpwindMode = [...modes]
    .sort((a, b) => a.start_index - b.start_index)
    .find((mode) => mode.start_index <= segment.end_index && mode.end_index >= segment.start_index);
  if (!firstUpwindMode) {
    return null;
  }
  const start = Math.max(firstUpwindMode.start_index, segment.start_index);
  const end = Math.min(firstUpwindMode.end_index, segment.end_index);
  return start <= end ? { start, end } : null;
};

const shouldShowStartLineForSelection = (
  segment: Segment | null | undefined,
  rangeOverride: RangeOverride | undefined,
  modes: EventFile['modes'] | undefined,
): boolean => {
  if (!segment) {
    return false;
  }
  if (segment.segment_type !== true) {
    return false;
  }
  if (!rangeOverride) {
    return true;
  }
  if (rangeOverride.kind === 'start') {
    return true;
  }
  const firstUpwindRange = getFirstUpwindLegRangeForSegment(segment, modes);
  return isSameIndexRange(rangeOverride, firstUpwindRange);
};

function datosCarrera(
  race: Segment,
  trackDataArray: TrackPoint[],
  modes: Array<{ start_index: number; end_index: number }>,
  tacks: Tack[],
) {
  const inicio = race.start_index;
  const fin = race.end_index;
  const puntosCarrera = trackDataArray.slice(inicio, fin + 1);
  if (puntosCarrera.length === 0) {
    return [];
  }

  const modosCarrera = modes
    .map((m) => {
      const start = Math.max(m.start_index, inicio);
      const end = Math.min(m.end_index, fin);
      return start <= end ? { start_index: start, end_index: end } : null;
    })
    .filter((m): m is { start_index: number; end_index: number } => m !== null);

  return modosCarrera.map((modo, index) => {
    const subPuntos = puntosCarrera.slice(
      Math.max(0, modo.start_index - inicio),
      Math.min(puntosCarrera.length, modo.end_index - inicio + 1),
    );
    let distancia = 0;
    let tws = 0;
    let vmgpc = 0;
    let sog = 0;
    let twa = 0;
    let heel = 0;
    let trim = 0;
    let rudder = 0;

    const tacksModo = tacks.filter(
      (tack) => tack.start_index >= modo.start_index && tack.end_index <= modo.end_index,
    );

    for (let index_ = 0; index_ < subPuntos.length; index_++) {
      const p = subPuntos[index_];
      tws += p.tws || 0;
      vmgpc += p.vmgpc || 0;
      sog += p.sog || 0;
      twa += Math.abs(p.twa || 0);
      heel += Math.abs(p.heel || 0);
      rudder += Math.abs(p.rudder || 0);
      trim += p.trim || 0;
      if (index_ > 0) {
        const previous = subPuntos[index_ - 1];
        distancia += distanciaPuntos(previous.lat, previous.lon, p.lat, p.lon);
      }
    }
    const c = subPuntos.length;
    return {
      mode: index + 1,
      metrics: [
        tws / c,
        vmgpc / c,
        sog / c,
        twa / c,
        heel / c,
        trim / c,
        rudder / c,
        distancia,
        tacksModo.length,
      ],
    };
  });
}
const renderCellValue = (value: number, decimals: number | 'integer') => {
  return decimals === 'integer' ? Math.round(value) : value.toFixed(decimals);
};
const splitPolarTextRow = (line: string): string[] =>
  line.includes(';') ? line.split(';') : line.split(/\s+/);

function parsePolarsTxt(txt: string): {
  angles: number[];
  tws: number[];
  speeds: number[][];
  stds: number[][];
} {
  const normalized = txt.trim();
  if (normalized.startsWith('{')) {
    try {
      const payload = JSON.parse(normalized) as unknown;
      const polars =
        payload && typeof payload === 'object' && 'polars' in payload
          ? (payload as { polars?: unknown }).polars
          : undefined;
      const boatSpeed =
        Array.isArray(polars) &&
        polars.find((item) => {
          if (!item || typeof item !== 'object') {
            return false;
          }
          const metric = String((item as { metric?: unknown }).metric ?? '')
            .toLowerCase()
            .split(/\s+/)
            .join('');
          return ['boatspeed', 'bsp', 'speed'].includes(metric);
        });
      const polarData =
        boatSpeed && typeof boatSpeed === 'object'
          ? (boatSpeed as { polarData?: unknown }).polarData
          : undefined;
      if (Array.isArray(polarData)) {
        const points = polarData
          .map((point) => {
            if (!point || typeof point !== 'object') {
              return null;
            }
            const raw = point as { tws?: unknown; twa?: unknown; metricValue?: unknown };
            const twsValue = Number(raw.tws);
            const twaValue = Number(raw.twa);
            const speedValue = Number(raw.metricValue);
            return Number.isFinite(twsValue) &&
              Number.isFinite(twaValue) &&
              Number.isFinite(speedValue)
              ? { tws: twsValue, twa: twaValue, speed: speedValue }
              : null;
          })
          .filter((point): point is { tws: number; twa: number; speed: number } => point !== null);

        const angles = [...new Set(points.map((point) => point.twa))].sort((a, b) => a - b);
        const tws = [...new Set(points.map((point) => point.tws))].sort((a, b) => a - b);
        const speeds = angles.map((angle) =>
          tws.map(
            (twsValue) =>
              points.find((point) => point.twa === angle && point.tws === twsValue)?.speed ?? 0,
          ),
        );
        const stds = angles.map(() => tws.map(() => 0));
        return { angles, tws, speeds, stds };
      }
    } catch {
      return { angles: [], tws: [], speeds: [], stds: [] };
    }
  }

  // por si llegan comas decimales
  const lines = txt
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  if (lines.length < 2) {
    return { angles: [], tws: [], speeds: [], stds: [] };
  }

  // Header
  const headerParts = splitPolarTextRow(lines[0]).map((s) => sanitize(s));
  // headerParts[0] suele ser "twa/tws"
  const tws = headerParts.slice(1).map(Number);

  const angles: number[] = [];
  const speeds: number[][] = [];
  const stds: number[][] = [];

  for (let index = 1; index < lines.length; index++) {
    const parts = splitPolarTextRow(lines[index]).map((s) => sanitize(s));
    if (parts.length < 2) {
      continue;
    }

    const angle = Number(parts[0]);
    angles.push(angle);

    const rowSpeed: number[] = [];
    const rowStd: number[] = [];

    for (let c = 1; c < parts.length; c++) {
      const cell = parts[c];
      // Formato "v±s" o "0.00±0.00"
      const match = cell.match(/^(-?\d+(?:\.\d+)?)\s*±\s*(-?\d+(?:\.\d+)?)$/);
      if (match) {
        const v = Number(match[1]);
        const s = Number(match[2]);
        rowSpeed.push(Number.isNaN(v) ? 0 : v);
        rowStd.push(Number.isNaN(s) ? 0 : s);
      } else {
        // valores tipo "0" o vacíos
        const v = Number(cell);
        rowSpeed.push(Number.isNaN(v) ? 0 : v);
        rowStd.push(0);
      }
    }

    // Rellenar hasta longitud de tws si faltan columnas
    while (rowSpeed.length < tws.length) {
      rowSpeed.push(0);
      rowStd.push(0);
    }

    speeds.push(rowSpeed);
    stds.push(rowStd);
  }

  return { angles, tws, speeds, stds };
}
// Group InfoRace items into ups and downs and assign displayMode starting at 1
function getGroupedModes(infoRace: InfoRaceItem[]): {
  ups: (InfoRaceItem & { displayMode: number })[];
  downs: (InfoRaceItem & { displayMode: number })[];
} {
  const ups = infoRace.filter((_, index) => index % 2 === 0);
  const downs = infoRace.filter((_, index) => index % 2 !== 0);

  const numberedUps = ups.map((up, index) => ({ ...up, displayMode: index + 1 }));
  const numberedDowns = downs.map((down, index) => ({
    ...down,
    displayMode: index + 1,
  }));

  return {
    ups: numberedUps,
    downs: numberedDowns,
  };
}

export const RenderizarRaces: React.FC<RenderizarRacesProperties> = ({
  races,
  trackDataArray,
  modes = [],
  tacks,
}) => {
  const { t } = useTranslation('common');
  const metricLabels = [
    t('map.raceStatsPanel.metrics.twsAvg', 'TWS AVG [kts]'),
    t('map.raceStatsPanel.metrics.vmgPercentAvg', 'VMG% AVG'),
    t('map.raceStatsPanel.metrics.sogAvg', 'SOG AVG [kts]'),
    t('map.raceStatsPanel.metrics.twaAvg', 'TWA AVG [º]'),
    t('map.raceStatsPanel.metrics.heelAvg', 'Heel AVG [º]'),
    t('map.raceStatsPanel.metrics.pitchAvg', 'Pitch AVG [º]'),
    t('map.raceStatsPanel.metrics.rudderAvg', 'Rudder AVG [º]'),
    t('map.raceStatsPanel.metrics.distance', 'Distance [m]'),
    t('map.raceStatsPanel.metrics.maneuvers', 'Maneuvers'),
  ];
  const raceFallback = t('map.raceStatsPanel.raceFallback', 'Race');
  const metricHeaderLabel = t('map.raceStatsPanel.table.metricHeader', 'Metric');

  const rowFormats: Array<number | 'integer'> = [1, 1, 1, 1, 1, 1, 1, 0, 'integer'];

  return (
    <>
      {races.map((race, index) => {
        const infoRace = datosCarrera(race, trackDataArray, modes, tacks);
        const { ups, downs } = getGroupedModes(infoRace);

        const raceNumberLabel = t('map.raceStatsPanel.raceNumber', {
          index: index + 1,
          defaultValue: `Race ${index + 1}`,
        });
        const titleBase =
          race.name && race.name.trim().length > 0
            ? race.name
            : races.length > 1
              ? raceNumberLabel
              : raceFallback;
        const title = titleBase;

        return (
          <div key={index}>
            <h3 className="tacks-header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>{title}</span>
            </h3>
            <table className="race-stats-table">
              <thead>
                <tr>
                  <th>{metricHeaderLabel}</th>
                  {ups.map((up, upIndex) => (
                    <th key={`up-${upIndex}`}>
                      {t('map.raceStatsPanel.table.upMode', {
                        mode: up.displayMode,
                        defaultValue: `Up ${up.displayMode}`,
                      })}
                    </th>
                  ))}
                  {downs.map((down, downIndex) => (
                    <th key={`down-${downIndex}`}>
                      {t('map.raceStatsPanel.table.downMode', {
                        mode: down.displayMode,
                        defaultValue: `Down ${down.displayMode}`,
                      })}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {metricLabels.map((label, index) => (
                  <tr key={index}>
                    <td>{label}</td>
                    {ups.map((up, upIndex) => (
                      <td key={`up-${upIndex}-${index}`}>
                        {renderCellValue(up.metrics[index], rowFormats[index])}
                      </td>
                    ))}
                    {downs.map((down, downIndex) => (
                      <td key={`down-${downIndex}-${index}`}>
                        {renderCellValue(down.metrics[index], rowFormats[index])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </>
  );
};
const combineSegments = (
  features: GeoJSON.Feature<GeoJSON.Geometry>[],
): GeoJSON.Feature<GeoJSON.Geometry>[] => {
  if (features.length === 0) {
    return [];
  }

  const combinedFeatures: GeoJSON.Feature<GeoJSON.Geometry>[] = [];
  let currentFeature: GeoJSON.Feature<GeoJSON.Geometry> = { ...features[0] };

  for (let index = 1; index < features.length; index++) {
    const feature = features[index];
    if (
      canMergeStyledLineFeatures(currentFeature.properties, feature.properties) &&
      currentFeature.geometry.type === 'LineString' &&
      feature.geometry.type === 'LineString'
    ) {
      currentFeature.geometry.coordinates.push(...feature.geometry.coordinates.slice(1));
    } else {
      combinedFeatures.push(currentFeature);
      currentFeature = { ...feature };
    }
  }
  combinedFeatures.push(currentFeature);

  return combinedFeatures;
};

type SegmentEditorState = {
  fileId: number;
  segment: Segment;
  range: { start: number; end: number };
  originalRange: { start: number; end: number };
  marks: Mark[];
  originalMarks: Mark[];
};

type ActiveSidebarSelection =
  | { type: 'file'; fileId: number }
  | { type: 'segment'; fileId: number; segmentId: number }
  | {
      type: 'leg';
      fileId: number;
      segmentId: number;
      start: number;
      end: number;
      kind: RangeSelectionKind;
    };

const SIDEBAR_ACTIVE_GROUP_CLASS = 'file-item-button-group--active';
const SIDEBAR_ACTIVE_PARENT_GROUP_CLASS = 'file-item-button-group--active-parent';
const SIDEBAR_ACTIVE_BUTTON_CLASS = 'file-item-button--active';
const SIDEBAR_ACTIVE_SEGMENT_BUTTON_CLASS = 'segment-item-button--active';
const SIDEBAR_ACTIVE_LEG_CLASS = 'is-active';

export const RaceInsightsPage: React.FC = () => {
  const navigate = useNavigate();
  const { startProcess, updateProcess, endProcess } = useProcessingOverlay();
  const [events, setEvents] = useState<Event[]>([]);

  const [isBoatDropdownOpen, setIsBoatDropdownOpen] = useState(false);

  const { t } = useTranslation('common');
  const translateSharing = useCallback(
    (key: string, fallback?: string) => t(key, { defaultValue: fallback }),
    [t],
  );
  const sharingLabel = useCallback(
    (sharing?: SharingMetadata | null) => buildSharingLabel(sharing, translateSharing),
    [translateSharing],
  );
  const EVENT_PLACEHOLDER = t(KEY_PLACEHOLDER_EVENT, LBL_EVENT_ELLIPSIS);
  const BOAT_PLACEHOLDER = t(KEY_PLACEHOLDER_BOAT, LBL_BOAT_ELLIPSIS);
  const DATE_PLACEHOLDER = t('race.placeholders.date', 'Date...');
  const [selectedDate, _setSelectedDate] = useState<string>(DATE_PLACEHOLDER);
  const [showSidebar, setShowSidebar] = useState(true);

  const [loaderKey, setLoaderKey] = useState(Date.now());
  const [isTrackLoadPending, setIsTrackLoadPending] = useState(false);

  const [isModalVisible, setIsModalVisible] = useState(false);

  const [eventFiles, setEventFiles] = useState<EventFile[]>([]);
  const [tacks, setTacks] = useState<Tack[]>([]);

  const [metricBounds, setMetricBounds] = useState<MetricBounds>({
    max: {
      vmg: 0,
      bsppc: 0,
      vmgpc: 0,
      bsp: 0,
      sog: 0,
      heel: 0,
      tws: 0,
      twa_abs: 0,
    },
    min: {
      vmg: 0,
      bsppc: 0,
      vmgpc: 0,
      bsp: 0,
      sog: 0,
      heel: 0,
      tws: 0,
      twa_abs: 0,
    },
    iqrMin: {
      vmg: 0,
      bsppc: 0,
      vmgpc: 0,
      bsp: 0,
      sog: 0,
      heel: 0,
      tws: 0,
      twa_abs: 0,
    },
    iqrMax: {
      vmg: 0,
      bsppc: 0,
      vmgpc: 0,
      bsp: 0,
      sog: 0,
      heel: 0,
      tws: 0,
      twa_abs: 0,
    },
  });

  const mapInstance1 = useRef<mapboxgl.Map | null>(null);

  const mapInstance2 = useRef<mapboxgl.Map | null>(null);

  const [showSaveTrainingModal, setShowSaveTrainingModal] = useState(false);
  const [selectedHighlightSegment, setSelectedHighlightSegment] = useState<HighlightSegment | null>(
    null,
  );

  const [highlightSegments, setHighlightSegments] = useState<HighlightSegment[]>([]);
  const highlightSegmentsRef = useRef<HighlightSegment[]>([]);
  useEffect(() => {
    highlightSegmentsRef.current = highlightSegments;
  }, [highlightSegments]);
  const [draftHighlight, setDraftHighlight] = useState<DraftHighlight | null>(null);
  const draftHighlightRef = useRef<DraftHighlight | null>(null);
  useEffect(() => {
    draftHighlightRef.current = draftHighlight;
  }, [draftHighlight]);

  const [cachedTrackData, setCachedTrackData] = useState<TrackPoint[]>([]);
  // ✅ versión corregida (claves number)
  const [cachedTacksGeoJSON, setCachedTacksGeoJSON] = useState<
    Record<number, GeoJSON.FeatureCollection | null>
  >({});

  const [lastFileId, setLastFileId] = useState<number | null>(null);
  const trackDataCacheRef = useRef(new Map<number, TrackDataCacheEntry>());
  const trackLoadTokenRef = useRef(0);
  const eventFetchTokenRef = useRef(0);
  const eventFilesFetchTokenRef = useRef(0);
  const isTrackLoadActive = useCallback((loadToken: number) => trackLoadTokenRef.current === loadToken, []);
  const isEventFetchActive = useCallback((fetchToken: number) => eventFetchTokenRef.current === fetchToken, []);
  const isEventFilesFetchActive = useCallback((fetchToken: number) => eventFilesFetchTokenRef.current === fetchToken, []);

  const eventSelectReference = useRef<HTMLDivElement>(null);
  const boatSelectReference = useRef<HTMLDivElement>(null);

  const [expandedFiles, setExpandedFiles] = useState<Record<number, boolean>>({});
  const [editingFileId, setEditingFileId] = useState<number | null>(null);
  const [editingFileText, setEditingFileText] = useState<string>('');
  const fileNameInputRef = useRef<HTMLInputElement | null>(null);
  const [editingSegmentId, setEditingSegmentId] = useState<number | null>(null);
  const [editingSegmentText, setEditingSegmentText] = useState<string>('');
  const renameLabel = t(RENAME_TRANSLATION_KEY, 'Rename');
  const deleteLabel = t('common.delete', 'Delete');
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    type: 'file' | 'segment';
    id: number;
    label?: string | null;
  } | null>(null);

  const raceLiteralDefault = 'Race';
  const raceFallbackLabel = t('map.raceStatsPanel.raceFallback', raceLiteralDefault);
  const trainingFallbackLabel = t('race.segmentEditor.training', 'Training');
  const raceNumberDefault = raceLiteralDefault;
  const trainingLiteralDefault = 'Training';

  const getSidebarSegmentDisplayName = (segment: Segment, fileId: number | null): string => {
    const parentFile = fileId ? eventFiles.find((f) => f.id === fileId) : null;
    if (segment.segment_type) {
      const races = parentFile?.segments.filter((s) => s.segment_type) ?? [];
      const raceIndex = races.findIndex((r) => r.id === segment.id);
      if (segment.name && segment.name.trim().length > 0) {
        return segment.name.trim();
      }
      if (raceIndex >= 0) {
        return t(RACE_NUMBER_KEY, {
          n: raceIndex + 1,
          defaultValue: `${raceNumberDefault} ${raceIndex + 1}`,
        });
      }
      return raceFallbackLabel;
    }
    if (segment.name && segment.name.trim().length > 0) {
      return segment.name.trim();
    }
    return `${trainingLiteralDefault} ${segment.id}`;
  };

  useEffect(() => {
    if (editingFileId !== null && fileNameInputRef.current) {
      fileNameInputRef.current.focus();
      fileNameInputRef.current.select();
    }
  }, [editingFileId]);

  const [trackData, setTrackData] = useState<TrackPoint[]>([]);
  const [primaryTimeRange, setPrimaryTimeRange] = useState<PrimaryTimeRange | null>(null);
  const [overlapSearchRange, setOverlapSearchRange] = useState<PrimaryTimeRange | null>(null);
  const primaryRangeBounds = useMemo(() => {
    if (!primaryTimeRange) {
      return null;
    }
    const startMs = new Date(primaryTimeRange.startUtc).getTime();
    const endMs = new Date(primaryTimeRange.endUtc).getTime();
    if (Number.isNaN(startMs) || Number.isNaN(endMs)) {
      return null;
    }
    return {
      startMs: Math.min(startMs, endMs),
      endMs: Math.max(startMs, endMs),
    };
  }, [primaryTimeRange]);
  const [marks, setMarks] = useState<Mark[]>([]);
  const [segmentEditor, setSegmentEditor] = useState<SegmentEditorState | null>(null);
  const [segmentEditorMode, setSegmentEditorMode] = useState<
    'idle' | 'addBuoy' | 'moveBuoy' | 'deleteBuoy'
  >('idle');
  const [polarsAngles, setPolarsAngles] = useState<number[]>([]);
  const [polarsTws, setPolarsTws] = useState<number[]>([]);
  const [polarsSpeeds, setPolarsSpeeds] = useState<number[][]>([]);
  const [polarsStds, setPolarsStds] = useState<number[][]>([]);
  const [polarsTxt, setPolarsTxt] = useState<string | null>(null);
  const hydrateTrackMetadata = useCallback((entry: TrackDataCacheEntry) => {
    setLoa(entry.loa);
    setPolarsTxt(entry.polarsTxt);
    setPolarsAngles(entry.polarsAngles);
    setPolarsTws(entry.polarsTws);
    setPolarsSpeeds(entry.polarsSpeeds);
    setPolarsStds(entry.polarsStds);
  }, []);
  const resetTrackDataCache = useCallback(() => {
    trackDataCacheRef.current.clear();
    setCachedTrackData([]);
    setLastFileId(null);
  }, []);
  const invalidateTrackDataCacheEntry = useCallback(
    (fileId: number) => {
      trackDataCacheRef.current.delete(fileId);
      if (lastFileId === fileId) {
        setCachedTrackData([]);
        setLastFileId(null);
      }
    },
    [lastFileId],
  );
  /**
   * Parsea un polars_txt en formato CSV con separador ';' y celdas "value±std".
   * Estructura esperada:
   *   Header: "twa/tws;4;6;8;...”
   *   Filas:  "<twa>;<v±s>;<v±s>;..."
   * Devuelve matrices separadas para velocidades y desviaciones.
   */

  const [userBoats, setUserBoats] = useState<Boat[]>([]);

  // Estado para el dropdown de barcos
  const [selectedBoatId, setSelectedBoatId] = useState<number | null>(null);
  const [selectedBoatName, setSelectedBoatName] = useState<string>(BOAT_PLACEHOLDER);

  // Estado para el dropdown de eventos (antes era "dates")
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [selectedEventName, setSelectedEventName] = useState<string>(EVENT_PLACEHOLDER);

  const [loa, setLoa] = useState<number>(1);

  const [selectedFile, setSelectedFile] = useState<EventFile | null>(null);
  const [activeSidebarSelection, setActiveSidebarSelection] =
    useState<ActiveSidebarSelection | null>(null);
  const [relatedFileCandidates, setRelatedFileCandidates] = useState<RelatedTrackFile[]>([]);
  const [relatedFileSegments, setRelatedFileSegments] = useState<Record<number, Segment[]>>({});
  const [selectedOverlayKeys, setSelectedOverlayKeys] = useState<string[]>([]);
  const [overlayTracks, setOverlayTracks] = useState<Record<number, TrackPoint[]>>({});
  const [overlayColors, setOverlayColors] = useState<Record<string, string>>({});
  const [isLoadingRelatedTracks, setIsLoadingRelatedTracks] = useState(false);
  const [isPrimaryTrackReady, setIsPrimaryTrackReady] = useState(false);
  const [primarySelectionType, setPrimarySelectionType] = useState<TrackSelectionType>('full');
  const [isRelatedTracksModalOpen, setIsRelatedTracksModalOpen] = useState(false);

  const selectedBoat = useMemo(
    () =>
      selectedBoatId == null
        ? null
        : (userBoats.find((boat) => boat.id === selectedBoatId) ?? null),
    [selectedBoatId, userBoats],
  );

  const selectedEvent = useMemo(
    () =>
      selectedEventId == null
        ? null
        : (events.find((event) => event.id === selectedEventId) ?? null),
    [events, selectedEventId],
  );

  const canManageSharing = useCallback(
    (sharing?: SharingMetadata | null) => {
      const contexts = [sharing, selectedEvent?.sharing, selectedBoat?.sharing];
      return contexts.every((entry) => isOwnedSharing(entry));
    },
    [selectedBoat?.sharing, selectedEvent?.sharing],
  );

  const requireOwnership = useCallback(
    (sharing?: SharingMetadata | null) => {
      const allowed = canManageSharing(sharing);
      if (!allowed) {
        showMessage(
          'error',
          t(NOTIF_ERROR),
          t('sharing.onlyMineActions', 'Solo puedes modificar tus propios datos'),
        );
      }
      return allowed;
    },
    [canManageSharing, t],
  );

  const findFileById = useCallback(
    (fileId: number | null | undefined) => eventFiles.find((file) => file.id === fileId) ?? null,
    [eventFiles],
  );

  const canManageSelectedFile = useMemo(
    () => canManageSharing(selectedFile?.sharing),
    [canManageSharing, selectedFile?.sharing],
  );

  const mapContainer1 = useRef<HTMLDivElement>(null);
  const mapContainer2 = useRef<HTMLDivElement>(null);
  const [showAllSegments, setShowAllSegments] = useState(false);

  const [showSidePage, setShowSidePage] = useState(false);

  const handleCloseRelatedTracksModal = () => {
    setIsRelatedTracksModalOpen(false);
  };

  const stopClosePropagation = (
    event:
      | React.PointerEvent<HTMLButtonElement>
      | React.MouseEvent<HTMLButtonElement>
      | React.MouseEvent<HTMLDivElement>
      | React.PointerEvent<HTMLDivElement>,
  ) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleRelatedTracksCloseClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    stopClosePropagation(event);
    handleCloseRelatedTracksModal();
  };

  // Añádelo junto al resto de useState y useEffect:

  const [globalToRelativeIndexMap, setGlobalToRelativeIndexMap] = useState<Record<number, number>>(
    {},
  );

  const [races, setRaces] = useState<Segment[]>([]);
  const [startLineSegments, setStartLineSegments] = useState<Segment[]>([]);

  // arriba del return:
  /* sustituye los useState existentes */
  const [panel1Mode, setPanel1Mode] = useState<PanelMode>('map');
  const [panel2Mode, setPanel2Mode] = useState<PanelMode>('none');
  const [panel3Mode, setPanel3Mode] = useState<PanelMode>('none');
  const [panel4Mode, setPanel4Mode] = useState<PanelMode>('none');
  const [expandedSegmentLegs, setExpandedSegmentLegs] = useState<Record<number, boolean>>({});

  const LAYOUT_LEFT_RIGHT: LayoutKey = 'left-right';
  // ⬇️ Sustituye la declaración original
  const [layoutMode, setLayoutMode] = useState<LayoutKey>(LAYOUT_LEFT_RIGHT);

  const mapInstance3 = useRef<mapboxgl.Map | null>(null);
  const mapInstance4 = useRef<mapboxgl.Map | null>(null);
  const sharedMapCameraRef = useRef<MapCameraState | null>(null);

  const mapContainer3 = useRef<HTMLDivElement>(null);
  const mapContainer4 = useRef<HTMLDivElement>(null);

  /** Devuelve sólo los mapas actualmente montados con su prefijo único */
  const getActiveMaps = useCallback(
    (): Array<{ map: mapboxgl.Map; prefix: string }> =>
      [
        { ref: mapInstance1, prefix: 'p1', mode: panel1Mode },
        { ref: mapInstance2, prefix: 'p2', mode: panel2Mode },
        { ref: mapInstance3, prefix: 'p3', mode: panel3Mode },
        { ref: mapInstance4, prefix: 'p4', mode: panel4Mode },
      ]
        .filter(({ ref, mode }) => mode === 'map' && ref.current)
        .map(({ ref, prefix }) => ({ map: ref.current as mapboxgl.Map, prefix })),
    [panel1Mode, panel2Mode, panel3Mode, panel4Mode],
  );
  const removeLegacyHighlightArtifacts = useCallback((map: mapboxgl.Map, prefix?: string) => {
    const layerPrefix = prefix
      ? `${LEGACY_HIGHLIGHT_LAYER_PREFIX}${prefix}-`
      : LEGACY_HIGHLIGHT_LAYER_PREFIX;
    const sourcePrefix = prefix
      ? `${LEGACY_HIGHLIGHT_SOURCE_PREFIX}${prefix}-`
      : LEGACY_HIGHLIGHT_SOURCE_PREFIX;
    const layers = map.getStyle()?.layers ?? [];

    for (const layer of layers) {
      if (!layer.id.startsWith(layerPrefix)) {
        continue;
      }
      const source = (layer as { source?: string }).source;
      map.removeLayer(layer.id);
      if (source && map.getSource(source)) {
        map.removeSource(source);
      }
    }

    const sources = map.getStyle()?.sources ?? {};
    for (const sourceId of Object.keys(sources)) {
      if (sourceId.startsWith(sourcePrefix) && map.getSource(sourceId)) {
        map.removeSource(sourceId);
      }
    }
  }, []);

  const removePreviewLayers = useCallback(() => {
    for (const { map, prefix } of getActiveMaps()) {
      removeLegacyHighlightArtifacts(map, prefix);
    }
  }, [getActiveMaps, removeLegacyHighlightArtifacts]);

  const renderHighlightCollectionOnMap = useCallback(
    (map: mapboxgl.Map, prefix: string, geo: HighlightFeatureCollection) => {
      const sourceId = `${HIGHLIGHT_SOURCE_ID_PREFIX}${prefix}`;
      const layerId = `${HIGHLIGHT_LAYER_ID_PREFIX}${prefix}`;
      removeLegacyHighlightArtifacts(map, prefix);

      if (map.getSource(sourceId)) {
        (map.getSource(sourceId) as mapboxgl.GeoJSONSource).setData(geo);
      } else {
        map.addSource(sourceId, { type: 'geojson', data: geo });
      }

      if (!map.getLayer(layerId)) {
        map.addLayer({
          id: layerId,
          type: 'line',
          source: sourceId,
          paint: {
            [LINE_COLOR_PROP]: ['get', 'color'],
            'line-width': 15,
            [LINE_OPACITY_PROP]: ['get', 'opacity'],
          },
          layout: {
            'line-cap': 'round',
            'line-join': 'round',
          },
        });
      }
      safeMoveLayer(map, layerId);
    },
    [removeLegacyHighlightArtifacts],
  );
  /* -------------------------------------------------------------------------- */

  /**
   * Elimina todas las capas y fuentes de highlight del mapa,
   * esperando al evento 'load' si el estilo aún no está listo.
   */
  function clearHighlightLayers(map: mapboxgl.Map): void {
    const wipe = (): void => {
      const layers = map.getStyle()?.layers ?? [];
      for (const layer of layers) {
        const id = layer.id;
        if (id.startsWith(HIGHLIGHT_LAYER_ID_PREFIX)) {
          const sourceId = (layer as { source?: string }).source;
          map.removeLayer(id);
          if (sourceId && map.getSource(sourceId)) {
            map.removeSource(sourceId);
          }
        }
      }
      removeLegacyHighlightArtifacts(map);
    };

    if (map.isStyleLoaded?.()) {
      wipe();
    } else {
      map.once('load', wipe);
    }
  }

  const resetHighlights = useCallback(() => {
    // limpia todos los mapas activos
    for (const { map } of getActiveMaps()) {
      clearHighlightLayers(map);
    }

    // vacía el estado → React ya no intentará redibujarlos
    setHighlightSegments([]);
    setDraftHighlight(null);
  }, [getActiveMaps]);

  /* ────────────── 2. HANDLERS NUEVOS  ────────────── */

  /* ——— Crea los SidePanelPage según layout ——— */
  // ——— Layout efectivo en función de los paneles activos ———
  const activePanels = [panel1Mode, panel2Mode, panel3Mode, panel4Mode].filter((m) => m !== 'none');

  const layoutCapacity: Record<LayoutKey, number> = {
    single: 1,
    [LAYOUT_LEFT_RIGHT]: 2,
    'top-bottom': 2,
    quadrants: 4,
    'two-top-one-bottom': 3,
    'one-top-two-bottom': 3,
    'two-left-one-right': 3,
    'one-left-two-right': 3,
  };
  const decideLayout = (p1: PanelMode, p2: PanelMode, p3: PanelMode, p4: PanelMode): LayoutKey => {
    const modes = [p1, p2, p3, p4];
    const active = modes.filter((m) => m !== 'none').length;

    switch (active) {
      case 0:
      case 1: {
        return 'single';
      }

      case 2: {
        // Mismo eje → “single row / single column”
        if (
          (modes[0] !== 'none' && modes[2] !== 'none') || // misma columna
          (modes[1] !== 'none' && modes[3] !== 'none')
        ) {
          return 'top-bottom';
        }
        return LAYOUT_LEFT_RIGHT;
      }

      case 3: {
        /* 3 paneles: identificamos qué cuadrante falta y elegimos
				   el layout que hace que el panel opuesto “crezca” para
				   rellenar todo el espacio libre                       */
        // Falta el top‑left  (idx 0) → un panel grande a la izquierda
        if (modes[0] === 'none') {
          return 'one-left-two-right';
        }
        // Falta el top‑right (idx 1) → un panel grande a la derecha
        if (modes[1] === 'none') {
          return 'two-left-one-right';
        }
        // Falta algún bottom (idx 2 o 3) → un panel grande abajo
        return 'two-top-one-bottom';
      }

      default: {
        return 'quadrants';
      }
    }
  };
  const effectiveLayout: LayoutKey =
    activePanels.length === layoutCapacity[layoutMode]
      ? layoutMode
      : decideLayout(panel1Mode, panel2Mode, panel3Mode, panel4Mode);

  /* Dentro de RaceInsightsPage.tsx */
  /* ---------------------------------------------------------------------- */
  /*  RaceInsightsPage.tsx                                                  */
  /*  Función renderPanels → versión corregida                              */
  /* ---------------------------------------------------------------------- */

  // 2️⃣ Función que mapea layout + índice original → posición CSS (grid-area)
  const getSideForByIndex = (layout: LayoutKey, originalIndex: number): SidePanelPosition => {
    switch (layout) {
      case 'quadrants': {
        return (['tl', 'tr', 'bl', 'br'] as SidePanelPosition[])[originalIndex] ?? 'tl';
      }
      case 'left-right': {
        return originalIndex === 0 || originalIndex === 2 ? 'l' : 'r';
      }
      case 'top-bottom': {
        return originalIndex <= 1 ? 't' : 'b';
      }
      case 'two-top-one-bottom': {
        // TL | TR | B
        if (originalIndex === 0) {
          return 'tl';
        }
        if (originalIndex === 1) {
          return 'tr';
        }
        return 'b';
      }
      case 'one-top-two-bottom': {
        // T | BL | BR
        if (originalIndex === 0) {
          return 't';
        }
        if (originalIndex === 2) {
          return 'bl';
        }
        return 'br';
      }
      case 'two-left-one-right': {
        // TL | BL | R
        if (originalIndex === 0) {
          return 'tl';
        }
        if (originalIndex === 2) {
          return 'bl';
        }
        return 'r';
      }
      case 'one-left-two-right': {
        // L | TR | BR
        if (originalIndex === 2) {
          return 'l';
        }
        if (originalIndex === 1) {
          return 'tr';
        }
        return 'br';
      }
      default: {
        return 'tl';
      }
    }
  };

  // Dentro de RaceInsightsPage.tsx:
  const renderPanels = () => {
    // 1️⃣ Definimos los 4 posibles paneles, y filtramos los desactivados (mode==='none')
    const allPanels = [
      { idx: 0, mode: panel1Mode, ref: mapInstance1, cont: mapContainer1 },
      { idx: 1, mode: panel2Mode, ref: mapInstance2, cont: mapContainer2 },
      { idx: 2, mode: panel3Mode, ref: mapInstance3, cont: mapContainer3 },
      { idx: 3, mode: panel4Mode, ref: mapInstance4, cont: mapContainer4 },
    ];
    const panelDefinitions = allPanels.filter(
      (panel): panel is (typeof allPanels)[number] & { mode: Exclude<PanelMode, 'none'> } =>
        panel.mode !== 'none',
    );

    const renderPanel = (panelDefinition: (typeof panelDefinitions)[number]) => {
      const panelId = `p${panelDefinition.idx + 1}` as PanelId;
      return (
        <SidePanelPage
          key={panelId}
          {...sidePanelCommonProperties}
          draftHighlight={draftHighlight}
          setDraftHighlight={setDraftHighlight}
          panelId={panelId}
          mode={panelDefinition.mode}
          mapInstance={panelDefinition.ref}
          mapContainer={panelDefinition.cont}
          side={getSideForByIndex(effectiveLayout, panelDefinition.idx)}
          layout={effectiveLayout}
        />
      );
    };

    /*
     * En cuatro cuadrantes, cada fila es su propio split horizontal. De este
     * modo el divisor vertical superior no altera los anchos de la fila
     * inferior (y viceversa), mientras el split entre filas sigue siendo
     * compartido para mantener un mosaico rectangular sin huecos ni solapes.
     */
    if (effectiveLayout === 'quadrants' && panelDefinitions.length === 4) {
      return [panelDefinitions.slice(0, 2), panelDefinitions.slice(2, 4)].map(
        (rowPanels, rowIndex) => (
          <div
            key={rowIndex === 0 ? 'quadrants-top-row' : 'quadrants-bottom-row'}
            className="quadrants-row"
            data-quadrants-row={rowIndex === 0 ? 'top' : 'bottom'}
          >
            {rowPanels.map((panelDefinition) => renderPanel(panelDefinition))}
          </div>
        ),
      );
    }

    return panelDefinitions.map((panelDefinition) => renderPanel(panelDefinition));
  };

  // Open PolarPanel on any polar button click
  const openPolarPanel = () => {
    // Ensure at least one panel visible and focused on Polar
    setPanel1Mode('polar');
    // Prefer single layout for a focused polar view
    setLayoutMode('single');
  };

  useEffect(() => {
    const button = document.querySelector(".polar-icon, #polar-button, [data-action='open-polar']");
    if (!button) {
      return;
    }
    const handler = () => openPolarPanel();
    button.addEventListener('click', handler);
    return () => button.removeEventListener('click', handler);
  }, []);

  /* RaceInsightsPage.tsx  ─── justo debajo de la declaración de layoutMode */
  useEffect(() => {
    /* ① Volvemos al tamaño “por defecto” SOLO al cambiar de layout */
    const mc = document.querySelector<HTMLDivElement>('.main-container');
    if (mc) {
      mc.style.gridTemplateColumns = '';
      mc.style.gridTemplateRows = '';
      for (const element of mc.querySelectorAll<HTMLElement>('.quadrants-row, .side-panel')) {
        element.style.flex = '';
      }
    }

    /* ② Y forzamos el resize de los mapas */
    for (const reference of [mapInstance1, mapInstance2, mapInstance3, mapInstance4]) {
      safeResizeMap(reference.current);
    }
  }, [effectiveLayout]);

  useEffect(() => {
    const map: Record<number, number> = {};
    for (const [relativeIndex, point] of trackData.entries()) {
      if (point.globalIndex !== undefined) {
        map[point.globalIndex] = relativeIndex;
      }
    }
    setGlobalToRelativeIndexMap(map);
  }, [trackData, resetHighlights]);

  useEffect(() => {
    const fetchEvents = async (boatId: number | null = null) => {
      const eventFetchToken = eventFetchTokenRef.current + 1;
      eventFetchTokenRef.current = eventFetchToken;
      if (boatId === null) {
        eventFilesFetchTokenRef.current += 1;
        setEvents([]);
        return;
      }
      try {
        const token = localStorage.getItem('token');
        const response = await axios.post<{
          success: boolean;
          events: Event[];
        }>(
          `${process.env.REACT_APP_API_BASE_URL ?? ''}/api/user/getEvents`,
          { boat_id: boatId },
          { headers: { Authorization: `Bearer ${token ?? ''}` } },
        );
        if (response.data && Array.isArray(response.data.events)) {
          const eventsData = Array.isArray(response.data.events) ? response.data.events : [];

          const sortedEvents = sortEvents(
            eventsData.map((event) => ({
              id: event.id,

              event_name: event.event_name,
              event_date: event.event_date,

              events: [], // Provide default or actual values
              sharing: (event as { sharing?: SharingMetadata }).sharing,
            })),
          );

          if (!isEventFetchActive(eventFetchToken)) {
            return;
          }
          setEvents(sortedEvents);
        } else {
        }
      } catch (error) {
        if (!isEventFetchActive(eventFetchToken)) {
          return;
        }
        console.error('Error fetching user events:', error);
      } finally {
      }
    };

    void fetchEvents(selectedBoatId);
  }, [selectedBoatId]);

  useEffect(() => {
    const fetchPreferredBoat = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL ?? ''}/api/user/getPreferredBoat`,
          {},
          { headers: { Authorization: `Bearer ${token ?? ''}` } },
        );
        if ((response.data as { success: boolean }).success) {
          const { boat } = response.data as { boat: Boat };
          // Actualizamos los estados para que el dropdown se llene con el barco preferido
          setSelectedBoatId(boat.id);
          setSelectedBoatName(boat.name);
        }
      } catch (error) {
        console.error('Error fetching preferred boat:', error);
      }
    };

    void fetchPreferredBoat();
  }, []);

  const existingColorsReference = useRef<string[]>([]); // `useRef` para almacenar los colores en un array mutable

  const generateRandomColor = (): string => {
    const mapColorScheme = resolveMapColorScheme(getCurrentColorScheme(), getCurrentMapColorMode());
    if (mapColorScheme === 'dark') {
      const paletteColor =
        DARK_MAP_HIGHLIGHT_COLORS.find(
          (color) => !existingColorsReference.current.includes(color),
        ) ??
        DARK_MAP_HIGHLIGHT_COLORS[
          existingColorsReference.current.length % DARK_MAP_HIGHLIGHT_COLORS.length
        ];
      existingColorsReference.current.push(paletteColor);
      return paletteColor;
    }

    if (existingColorsReference.current.length === 0) {
      const defaultColor = 'rgba(255, 255, 0, 0.75)';
      existingColorsReference.current.push(defaultColor);
      return defaultColor;
    }

    let color;
    let isValid;
    do {
      const r = Math.floor(Math.random() * 256);
      const g = Math.floor(Math.random() * 256);
      const b = Math.floor(Math.random() * 256);
      const alpha = 0.75;
      color = `rgba(${r}, ${g}, ${b}, ${alpha})`;

      isValid = existingColorsReference.current.every((existingColor) => {
        const [er, eg, eb] = existingColor.match(/\d+/g)!.map(Number);
        return Math.hypot(r - er, g - eg, b - eb) >= 75;
      });
    } while (!isValid);

    existingColorsReference.current.push(color); // Agregar el color generado al array de referencia
    return color;
  };

  const resetOverlayContext = useCallback(() => {
    setRelatedFileCandidates([]);
    setRelatedFileSegments({});
    setSelectedOverlayKeys([]);
    setOverlayTracks({});
    setOverlayColors({});
    setIsLoadingRelatedTracks(false);
    setPrimaryTimeRange(null);
    setOverlapSearchRange(null);
    setIsPrimaryTrackReady(false);
    setPrimarySelectionType('full');
    setActiveSidebarSelection(null);
  }, []);

  const clearLoadedTrackState = useCallback(() => {
    setTrackData([]);
    setCachedTrackData([]);
    setLastFileId(null);
    setTacks([]);
    setRaces([]);
    setStartLineSegments([]);
    setMarks([]);
    setSelectedFile(null);
    setPrimaryTimeRange(null);
    setOverlapSearchRange(null);
    setIsPrimaryTrackReady(false);
    setGlobalToRelativeIndexMap({});
    setActiveSidebarSelection(null);
  }, []);

  const refreshRelatedTrackFiles = useCallback(
    async (fileId: number | null, timeRange?: PrimaryTimeRange | null) => {
      console.debug('[relatedTracks] refreshRelatedTrackFiles:start', {
        fileId,
        timeRange,
      });
      if (!fileId || !timeRange) {
        setRelatedFileCandidates([]);
        setIsLoadingRelatedTracks(false);
        return;
      }
      type RelatedFilesApiResponse = {
        success: boolean;
        data?: { relatedFiles?: RelatedTrackFile[] };
      };
      setIsLoadingRelatedTracks(true);
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.post<RelatedFilesApiResponse>(
          `${process.env.REACT_APP_API_BASE_URL ?? ''}/api/track/relatedFiles`,
          {
            fileId,
            primaryStartTime: timeRange.startUtc,
            primaryEndTime: timeRange.endUtc,
          },
          { headers: { Authorization: `Bearer ${token ?? ''}` } },
        );
        if (data?.success && Array.isArray(data.data?.relatedFiles)) {
          console.debug('[relatedTracks] API success', {
            count: data.data.relatedFiles.length,
            overlapSeconds: data.data.relatedFiles.map((item) => item.overlapSeconds),
          });
          setRelatedFileCandidates(data.data.relatedFiles);
        } else {
          console.debug('[relatedTracks] API empty/invalid payload');
          setRelatedFileCandidates([]);
        }
      } catch (error) {
        console.error('Error fetching related files:', error);
        setRelatedFileCandidates([]);
      } finally {
        setIsLoadingRelatedTracks(false);
        console.debug('[relatedTracks] refreshRelatedTrackFiles:end');
      }
    },
    [],
  );

  // Función para crear el GeoJSON de la rejilla

  const sortEvents = (events: Event[]): Event[] => {
    return [...events].sort((a, b) => {
      const timeA = a.event_date ? new Date(a.event_date).getTime() : null;
      const timeB = b.event_date ? new Date(b.event_date).getTime() : null;
      if (timeA !== null && timeB !== null) {
        return timeB - timeA;
      }
      if (timeA !== null) {
        return -1;
      }
      if (timeB !== null) {
        return 1;
      }
      return a.event_name.localeCompare(b.event_name);
    });
  };
  const getAverageBsp = (indices: number[]): number => {
    const validBspValues = indices
      .map((index) => trackData[index]?.bsp)
      .filter((bsp): bsp is number => bsp !== null && bsp !== undefined);
    if (validBspValues.length === 0) {
      return 0;
    }
    return validBspValues.reduce((sum, bsp) => sum + bsp, 0) / validBspValues.length;
  };
  const getTackColor = (startIndex: number, endIndex: number): string => {
    // Asegúrate de que los índices están dentro de los límites
    const validStartIndices = Array.from({ length: 10 }, (_, index) => startIndex - index).filter(
      (index) => index >= 0,
    );
    const validEndIndices = Array.from({ length: 10 }, (_, index) => endIndex + index).filter(
      (index) => index < trackData.length,
    );

    // Calcular los promedios
    const startBspAvg = getAverageBsp(validStartIndices);
    const endBspAvg = getAverageBsp(validEndIndices);

    // Lógica de colores
    const bspRatio = endBspAvg / startBspAvg;

    if (bspRatio >= 0.9) {
      return 'green';
    }
    if (bspRatio >= 0.7 && bspRatio < 0.9) {
      return 'orange';
    }
    return 'red';
  };
  /**
   * Descarga los tacks para un fileId dado y los cachea para evitar recálculos si se vuelven a pedir.
   * @param fileId Identificador del archivo (track) del que se quieren los tacks.
   */
  // ⬇️  FUNCIÓN fetchTacks COMPLETA (reemplázala tal cual)
  // ⬇️  NUEVA función utilitaria (colócala encima de fetchTacks o en el grupo de helpers)

  const buildTacksGeoJSON = (
    tacksArray: Tack[],
    trackFull: TrackPoint[],
  ): GeoJSON.FeatureCollection => {
    const features: GeoJSON.Feature<GeoJSON.Geometry>[] = tacksArray
      .map((tack, tackIndex) => {
        const slice = trackFull.slice(tack.start_index, tack.end_index + 1);
        if (slice.length === 0) {
          return null;
        }

        const coordinates = slice.map((p) => [p.lon, p.lat]);
        const stats = calcularEstadisticasTack(slice, trackFull); // ⬅️ reutilizamos tu helper
        const color = getTackColor(tack.start_index, tack.end_index);

        return {
          type: 'Feature',
          geometry: { type: 'LineString', coordinates },
          properties: {
            tackIndex,
            color,
            maxRudder: stats.maxRudder,
            timeToMaxRudder: stats.timeToMaxRudder,
            avgSogBeforeTack: stats.avgSogBeforeTack,
            avgTwaBeforeTack: stats.avgTwaBeforeTack,
            SogMin: stats.SogMin,
            timeToRecovery90: stats.timeToRecovery90,
            timeToRecovery95: stats.timeToRecovery95,
            type: stats.type,
          },
        };
      })
      .filter(Boolean) as GeoJSON.Feature<GeoJSON.Geometry>[];

    return { type: 'FeatureCollection', features } as GeoJSON.FeatureCollection;
  };
  const fetchTacks = async (
    fileId: number,
    range?: { start: number; end: number },
    trackFullOverride?: TrackPoint[],
    loadToken?: number,
  ): Promise<Tack[]> => {
    // Guardamos estado anterior para rollback

    try {
      // ① Llamada al backend (POST siempre)
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL ?? ''}/api/track/getTacks`,
        { file_id: fileId },
        { headers: { Authorization: `Bearer ${token ?? ''}` } },
      );

      // ② Control de éxito
      if (!(response.data as { success: boolean }).success) {
        // Error controlado desde el backend
        const apiMessage =
          typeof (response.data as { message?: unknown })?.message === 'string'
            ? (response.data as { message: string }).message
            : 'Unknown error';
        throw new Error(apiMessage);
      }

      // ③ Datos completos y cache
      const allTacks: Tack[] = (response.data as { data: Tack[] }).data;

      // ④ Filtrado opcional por rango
      const filteredTacks = range
        ? allTacks.filter((t) => t.start_index >= range.start && t.end_index <= range.end)
        : allTacks;

      if (loadToken !== undefined && !isTrackLoadActive(loadToken)) {
        return [];
      }

      // ⑤ Actualización de estado
      setTacks(filteredTacks);

      // ⑥ Reconstrucción de GeoJSON para renderizado rápido
      const trackForGeo = trackFullOverride ?? cachedTrackData;
      if (filteredTacks.length > 0 && trackForGeo.length > 0) {
        const geo = buildTacksGeoJSON(filteredTacks, trackForGeo);
        setCachedTacksGeoJSON((previous) => ({
          ...previous,
          [fileId]: geo,
        }));
      }

      return filteredTacks;
    } catch {
      return [];
    }

    // ← RESPALDO para que TS sepa que siempre devolvemos un array
    return [];
  };

  const fetchTrackData = async (
    fileId: number,
    options?: { hydrateState?: boolean; forceRefresh?: boolean; loadToken?: number },
  ): Promise<FetchTrackDataResponse> => {
    const shouldHydrate = options?.hydrateState !== false;
    const canHydrateState =
      shouldHydrate && (options?.loadToken === undefined || isTrackLoadActive(options.loadToken));
    const cachedTrack = trackDataCacheRef.current.get(fileId);
    if (cachedTrack && !options?.forceRefresh) {
      if (canHydrateState) {
        hydrateTrackMetadata(cachedTrack);
      }
      return {
        success: true,
        data: {
          trackDataArray: cachedTrack.trackDataArray,
          loa: cachedTrack.loa,
        },
      };
    }
    // Strongly type the API response to avoid unsafe assignments
    type TrackDataApiResponse = {
      success: boolean;
      data?: {
        trackDataRaw?: TrackDatum[]; // see type alias declared below in the file
        trackDataRows?: TrackDataPackedRow[];
        trackDataColumns?: string[];
        loa?: number;
        polars_txt?: string | null;
      };
      message?: string;
    };

    try {
      const { data } = await axios.post<TrackDataApiResponse>(
        `${process.env.REACT_APP_API_BASE_URL ?? ''}/api/track/trackData`,
        { fileId },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token') ?? ''}` } },
      );

      if (!data?.success) {
        // Backend may include a safe message. Do not expose sensitive info.
        // Return controlled shape to the UI.
        return { success: false, data: { trackDataArray: [], loa: 0 } };
      }

      // Defensive extraction with runtime checks to satisfy eslint no-unsafe-assignment
      const payload = data.data ?? {};
      const trackDataRowsUnsafe = payload.trackDataRows;
      const trackDataRawUnsafe = payload.trackDataRaw;
      const hasPackedTrackRows = Array.isArray(trackDataRowsUnsafe);
      const trackDataRaw: TrackDatum[] = Array.isArray(trackDataRowsUnsafe)
        ? unpackTrackDataRows(trackDataRowsUnsafe)
        : Array.isArray(trackDataRawUnsafe)
          ? trackDataRawUnsafe
          : [];

      const loaValue = typeof payload.loa === 'number' ? payload.loa : 0;
      const polarsTxtResp = typeof payload.polars_txt === 'string' ? payload.polars_txt : null;
      const polarsTxtValue =
        polarsTxtResp && polarsTxtResp.trim().length > 0 ? polarsTxtResp : null;
      const parsedPolars = polarsTxtValue
        ? parsePolarsTxt(polarsTxtValue)
        : { angles: [], tws: [], speeds: [], stds: [] };

      if (canHydrateState) {
        setLoa(loaValue);
        setPolarsTxt(polarsTxtValue);
        setPolarsAngles(parsedPolars.angles);
        setPolarsTws(parsedPolars.tws);
        setPolarsSpeeds(parsedPolars.speeds);
        setPolarsStds(parsedPolars.stds);
      }

      if (trackDataRaw.length > 0) {
        // Attach globalIndex for later reverse mapping
        const trackDataIndex = hasPackedTrackRows
          ? trackDataRaw
          : trackDataRaw.map((point, index) => ({
              ...point,
              globalIndex: index,
            }));

        const trackDataInterp = interpolateTrackData(trackDataIndex, [
          'vmgpc',
          'bsp',
          'sog',
          'rudder',
          'heel',
          'twa',
          'tws',
          'hdg',
        ]) as unknown as TrackPoint[];

        const trackData = trackDataInterp.map((p: TrackPoint, index: number) => {
          const sog = p.sog ?? 0;
          const twa = p.twa ?? 0;
          const vmg = Math.abs(sog * Math.cos((twa * Math.PI) / 180));
          const twa_abs = p.twa == null ? null : Math.abs(p.twa);
          const rudder_abs = p.rudder == null ? null : Math.abs(p.rudder);

          let rot: number | undefined;
          if (index > 0) {
            const previous = trackDataInterp[index - 1] as TrackPoint | undefined;
            if (
              previous &&
              previous.hdg !== null &&
              previous.hdg !== undefined &&
              p.hdg !== undefined &&
              previous.utc !== undefined &&
              p.utc !== undefined
            ) {
              const headingDiff = (p.hdg as number) - previous.hdg;
              const utcPrevious = new Date(previous.utc as unknown as number).getTime();
              const utcCurrent = new Date(p.utc as unknown as number).getTime();
              const utcDiff = (utcCurrent - utcPrevious) / 1000;
              rot = utcDiff === 0 ? undefined : headingDiff / utcDiff;
            }
          }

          return {
            ...p,
            utc: String(p.utc ?? ''),
            lat: p.lat,
            lon: p.lon,
            twd: p.twd ?? 0,
            vmg: Number.isFinite(vmg) ? vmg : undefined,
            twa_abs: twa_abs ?? undefined,
            rudder_abs: rudder_abs ?? undefined,
            rot: rot ?? undefined,
          };
        });

        trackDataCacheRef.current.set(fileId, {
          trackDataArray: trackData,
          loa: loaValue,
          polarsTxt: polarsTxtValue,
          polarsAngles: parsedPolars.angles,
          polarsTws: parsedPolars.tws,
          polarsSpeeds: parsedPolars.speeds,
          polarsStds: parsedPolars.stds,
        });
        return { success: true, data: { trackDataArray: trackData, loa: loaValue } };
      }

      return { success: false, data: { trackDataArray: [], loa: 0 } };
    } catch (error) {
      console.error('Error fetching track data:', error);
      // Reset polars on failure
      if (canHydrateState) {
        setPolarsTxt(null);
        setPolarsAngles([]);
        setPolarsTws([]);
        setPolarsSpeeds([]);
        setPolarsStds([]);
      }
      return { success: false, data: { trackDataArray: [], loa: 0 } };
    }
  };

  // Strictly typed interpolation to avoid unsafe `any` access
  type InterpolableField =
    | 'vmgpc'
    | 'bsp'
    | 'sog'
    | 'heel'
    | 'twa'
    | 'tws'
    | 'rudder'
    | 'hdg'
    | 'trim';

  type TrackDatum = {
    utc: string | number;
    lat: number;
    lon: number;
    twd: number | null;
    tmtogun?: number | null;
    globalIndex?: number;
  } & { [K in InterpolableField]: number | null };

  type TrackDataPackedRow = [
    string | number,
    number,
    number,
    number | null,
    number | null,
    number | null,
    number | null,
    number | null,
    number | null,
    number | null,
    number | null,
    number | null,
    number | null,
    number | null,
    number | null,
  ];

  const unpackTrackDataRows = (rows: TrackDataPackedRow[]): TrackDatum[] =>
    rows.map((row, globalIndex) => ({
      utc: row[0],
      lat: row[1],
      lon: row[2],
      twd: row[3],
      globalIndex,
      vmgpc: row[4],
      bsppc: row[5],
      bsp: row[6],
      sog: row[7],
      heel: row[8],
      twa: row[9],
      tws: row[10],
      rudder: row[11],
      hdg: row[12],
      trim: row[13],
      tmtogun: row[14],
    }));

  const interpolateTrackData = (trackData: TrackDatum[], fields: InterpolableField[]) => {
    // Clone objects to avoid mutating original array elements
    const filledData = trackData.map((p) => ({ ...p }));

    for (const field of fields) {
      let lastValidIndex: number | null = null;

      for (let index = 0; index < filledData.length; index++) {
        const value = filledData[index][field];
        if (value !== null && value !== undefined) {
          // If we found a valid value, linearly interpolate the gap since the last valid index
          if (lastValidIndex !== null && lastValidIndex !== index - 1) {
            const lastValue = filledData[lastValidIndex][field]!;
            const currentValue = value;
            const gap = index - lastValidIndex;

            for (let k = 1; k < gap; k++) {
              const fraction = k / gap;
              filledData[lastValidIndex + k][field] =
                lastValue + fraction * (currentValue - lastValue);
            }
          }
          lastValidIndex = index;
        }
      }

      // Fill leading nulls with the nearest subsequent value
      for (let index = 0; index < filledData.length; index++) {
        const value = filledData[index][field];
        if (value !== null && value !== undefined) {
          break;
        }
        if (index + 1 < filledData.length) {
          filledData[index][field] = filledData[index + 1][field];
        }
      }

      // Fill trailing nulls with the nearest previous value
      for (let index = filledData.length - 1; index >= 0; index--) {
        const value = filledData[index][field];
        if (value !== null && value !== undefined) {
          break;
        }
        if (index - 1 >= 0) {
          filledData[index][field] = filledData[index - 1][field];
        }
      }
    }

    return filledData;
  };

  // Uso de la función con los campos que deseas interpolar

  // Función que actualiza el tamaño y la posición del panel según la dirección y el movimiento del mouse
  /*********************/
  /* FUNCIÓN AUXILIAR  */
  /*********************/
  // Función para actualizar el tamaño y la posición del panel según la dirección y el movimiento del mouse
  // (Modificable para splitters en layouts avanzados)
  // (Esta función puede ser llamada desde un handler de mousemove)

  // Actualizar automáticamente al cambiar el estado de `showTacks`

  // Escuchar cambios en `showTacks` y eliminar los tacks si se apagan

  // Función para actualizar la leyenda de color según la métrica seleccionada

  const handleBoatChange = (value: string) => {
    eventFetchTokenRef.current += 1;
    eventFilesFetchTokenRef.current += 1;
    const selectedBoat = userBoats.find((boat) => boat.name === value);
    if (selectedBoat) {
      setSelectedBoatId(selectedBoat.id);
      setSelectedBoatName(selectedBoat.name);
    } else {
      setSelectedBoatId(null);
      setSelectedBoatName(t(KEY_PLACEHOLDER_BOAT, LBL_BOAT_ELLIPSIS));
    }
    // Resetea el custom select de Event al valor por defecto
    setSelectedEventId(null);
    setSelectedEventName(t(KEY_PLACEHOLDER_EVENT, LBL_EVENT_ELLIPSIS));
    setEvents([]);
    setEventFiles([]);
    resetTrackDataCache();
    clearLoadedTrackState();
    resetOverlayContext();
  };

  // Función para calcular la distancia horizontal en kilómetros
  // (Esta función puede ser la misma que ya tienes para calcular la distancia geodésica)
  const handleDeleteFile = async (fileId: number) => {
    const targetFile = findFileById(fileId);
    if (!requireOwnership(targetFile?.sharing)) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL ?? ''}/api/user/deleteFile`,
        { fileId },
        { headers: { Authorization: `Bearer ${token ?? ''}` } },
      );
      if ((response.data as { success: boolean }).success) {
        // Remove from local state
        setEventFiles((previous) => previous.filter((file) => file.id !== fileId));
        invalidateTrackDataCacheEntry(fileId);
        if (lastFileId === fileId) {
          setSelectedFile(null);
          setTrackData([]);
          resetOverlayContext();
        }
        showMessage('success', t('notifications.success'), t('reports.deleteSuccess'));
      } else {
        showMessage('error', t(NOTIF_ERROR), t('reports.deleteReportFail'));
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      showMessage('error', t(NOTIF_ERROR), t('reports.deleteReportFail'));
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmation) {
      return;
    }
    const { type, id } = deleteConfirmation;
    setDeleteConfirmation(null);
    await (type === 'file' ? handleDeleteFile(id) : handleDeleteSegment(id));
  };

  // justo dentro del componente RaceInsightsPage, junto al resto de hooks y funciones:

  // Función para calcular metros por píxel en función del zoom y latitud

  // Función para calcular la distancia geodésica entre dos puntos (Haversine)

  // Modifica `handleEventChange` para cargar los archivos del evento

  const handleEventChange = useCallback(
    async (value: string) => {
      if (!value || value === EVENT_PLACEHOLDER) {
        eventFilesFetchTokenRef.current += 1;
        setSelectedEventId(null);
        setSelectedEventName(EVENT_PLACEHOLDER);
        setEventFiles([]);
        resetTrackDataCache();
        clearLoadedTrackState();
        resetOverlayContext();

        return;
      }
      const selectedEventIdFromValue = Number.parseInt(value, 10);
      const selectedEvent = events.find((event) => event.id === selectedEventIdFromValue);
      if (!selectedEvent) {
        return;
      }
      const eventFilesFetchToken = eventFilesFetchTokenRef.current + 1;
      eventFilesFetchTokenRef.current = eventFilesFetchToken;
      setSelectedEventId(selectedEvent.id);
      setSelectedEventName(selectedEvent.event_name);
      setEditingFileId(null);
      setEditingFileText('');
      resetTrackDataCache();
      clearLoadedTrackState();

      try {
        // 1) Insert ApiEventFilesResponse interface just before axios.post
        interface ApiEventFilesResponse {
          success: boolean;
          event?: {
            files?: Array<{
              id: number;
              date: string | number | Date | null;
              name?: string | null;
              filename?: string | null;
              display_name?: string | null;
              sharing?: SharingMetadata;
            }>;
            sharing?: SharingMetadata;
          };
          message?: string;
        }
        // 2) Typed axios.post call
        const token = localStorage.getItem('token');
        const response = await axios.post<ApiEventFilesResponse>(
          `${process.env.REACT_APP_API_BASE_URL ?? ''}/api/user/getEventsFiles`,
          { filter: 'all', event_id: selectedEvent.id },
          { headers: { Authorization: `Bearer ${token ?? ''}` } },
        );
        if (!isEventFilesFetchActive(eventFilesFetchToken)) {
          return;
        }
        // 3) Typed conditional block for checking files
        if (
          response.data?.success === true &&
          response.data.event &&
          Array.isArray(response.data.event.files)
        ) {
          const eventSharing: SharingMetadata | undefined =
            (response.data.event as { sharing?: SharingMetadata | null }).sharing ??
            selectedEvent?.sharing ??
            undefined;
          const filesFromAPI = response.data.event.files;
          const filesWithDetails = await Promise.all(
            filesFromAPI.map(async (file) => {
              try {
                const segmentsResponse = await axios.post<{ success: boolean; data: Segment[] }>(
                  `${process.env.REACT_APP_API_BASE_URL ?? ''}/api/track/getSegments`,
                  { fileId: file.id },
                  { headers: { Authorization: `Bearer ${token ?? ''}` } },
                );
                const modesResponse = await axios.post<{
                  success: boolean;
                  data: EventFile['modes'];
                }>(
                  `${process.env.REACT_APP_API_BASE_URL ?? ''}/api/track/getModes`,
                  { fileId: file.id },
                  { headers: { Authorization: `Bearer ${token ?? ''}` } },
                );
                const segments =
                  (segmentsResponse.data?.data as Segment[] | undefined)?.sort(
                    (a: Segment, b: Segment) => a.start_index - b.start_index,
                  ) ?? [];
                const modes =
                  (modesResponse.data?.data as EventFile['modes'] | undefined)?.sort(
                    (a, b) => a.start_index - b.start_index,
                  ) ?? [];
                return {
                  ...file,
                  segments,
                  modes,
                };
              } catch (fileDetailsError) {
                console.error('Error fetching file details:', fileDetailsError);
                return {
                  ...file,
                  segments: [],
                  modes: [],
                };
              }
            }),
          );
          if (!isEventFilesFetchActive(eventFilesFetchToken)) {
            return;
          }
          const formattedFiles: EventFile[] = filesWithDetails.map((file, index) => {
            const dateYmd = formatDateYMD(file.date);
            const rawDate =
              typeof file.date === 'number' ||
              typeof file.date === 'string' ||
              file.date instanceof Date
                ? new Date(file.date)
                : null;
            const normalizedDate =
              rawDate && !Number.isNaN(rawDate.getTime()) ? rawDate.toISOString() : null;
            const fallbackName = dateYmd ?? `File ${index + 1}`;
            const providedNameRaw =
              typeof file.display_name === 'string' && file.display_name.trim().length > 0
                ? file.display_name.trim()
                : typeof file.name === 'string' && file.name.trim().length > 0
                  ? file.name.trim()
                  : typeof file.filename === 'string' && file.filename.trim().length > 0
                    ? file.filename.trim()
                    : null;
            const providedNameDate = formatDateYMD(providedNameRaw);
            const providedName =
              providedNameDate ??
              (providedNameRaw && providedNameRaw.includes('T')
                ? providedNameRaw.split('T')[0]
                : providedNameRaw);
            const finalName = providedName ?? fallbackName;
            const usedAutoName = providedName == null || providedNameDate === dateYmd;

            const sharing =
              (file as { sharing?: SharingMetadata | null }).sharing ?? eventSharing ?? undefined;
            return {
              id: file.id,
              name: finalName ?? fallbackName,
              date: normalizedDate,
              segments: file.segments,
              modes: file.modes,
              sharing,
              autoName: usedAutoName,
            };
          });

          setEventFiles(formattedFiles);
        } else {
          if (!isEventFilesFetchActive(eventFilesFetchToken)) {
            return;
          }
          setEventFiles([]);
          clearLoadedTrackState();
          resetOverlayContext();
        }
      } catch (error) {
        if (!isEventFilesFetchActive(eventFilesFetchToken)) {
          return;
        }
        console.error('Error fetching event files:', error);
        setEventFiles([]);
        clearLoadedTrackState();
        resetOverlayContext();
      }
    },
    [
      EVENT_PLACEHOLDER,
      clearLoadedTrackState,
      events,
      isEventFilesFetchActive,
      resetOverlayContext,
      resetTrackDataCache,
    ],
  );

  useEffect(() => {
    if (events.length === 0) {
      setEventFiles([]);
      setSelectedFile(null);
      return;
    }
    const currentEvent = selectedEventId
      ? events.find((event) => event.id === selectedEventId)
      : null;
    if (currentEvent) {
      return;
    }
    const newestEvent = events.at(0);
    if (!newestEvent?.id) {
      return;
    }
    void handleEventChange(String(newestEvent.id));
  }, [events, handleEventChange, selectedEventId]);

  // useEffect para manejar el evento 'mousemove' en el mapa para los updates en tiempo real

  const handleLogoClick = () => {
    navigate('/home');
  };

  const handleClickOutside = (error: MouseEvent) => {
    if (
      boatSelectReference.current &&
      !boatSelectReference.current.contains(error.target as Node)
    ) {
      setIsBoatDropdownOpen(false);
    }
    if (
      eventSelectReference.current &&
      !eventSelectReference.current.contains(error.target as Node)
    ) {
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  const toggleModal = () => {
    setIsModalVisible((previousState) => !previousState);
  };

  const toggleFileExpansion = (fileId: number) => {
    setExpandedFiles((previous) => ({
      ...previous,
      [fileId]: !previous[fileId],
    }));

    setShowAllSegments((previous) => (previous ? false : previous));
  };

  // Precompute maximum metric values when trackData changes
  /**
   * Devuelve los límites absolutos e IQR (acotados) de un array numérico.
   */

  // ------------- dentro del useEffect que calcula metricBounds -------------
  useEffect(() => {
    if (trackData.length === 0) {
      return;
    }

    // Limit metric keys to those defined in MetricBounds
    type MetricKey = keyof MetricBounds['max'];
    const metrics: MetricKey[] = ['vmg', 'bsppc', 'vmgpc', 'bsp', 'sog', 'tws', 'heel', 'twa_abs'];

    const newMax: Record<MetricKey, number> = {} as Record<MetricKey, number>;
    const newMin: Record<MetricKey, number> = {} as Record<MetricKey, number>;
    const newIqrMin: Record<MetricKey, number> = {} as Record<MetricKey, number>;
    const newIqrMax: Record<MetricKey, number> = {} as Record<MetricKey, number>;

    for (const metric of metrics) {
      const values = trackData
        .map((p) => p[metric])
        .filter((v): v is number => isFiniteMetricValue(v));

      if (metric === 'vmgpc') {
        newMin[metric] = VMGPC_COLOR_SCALE_MIN;
        newMax[metric] = VMGPC_COLOR_SCALE_MAX;
        newIqrMin[metric] = VMGPC_COLOR_SCALE_MIN;
        newIqrMax[metric] = VMGPC_COLOR_SCALE_MAX;
        continue;
      }

      const { min, max, iqrMin, iqrMax } = computeMetricBoundsIQR(values);

      newMax[metric] = max;
      newMin[metric] = min;
      newIqrMin[metric] = iqrMin;
      newIqrMax[metric] = iqrMax;
    }

    setMetricBounds({
      max: newMax,
      min: newMin,
      iqrMin: newIqrMin,
      iqrMax: newIqrMax,
    });
  }, [trackData]);

  const cargarDatosDeArchivo = async (
    fileId: number,
    segmentIndex: number | undefined,
    rangeOverride: RangeOverride | undefined,
    loadToken: number,
  ): Promise<TrackPoint[]> => {
    const shouldRefreshRelatedTracks = lastFileId !== fileId;
    if (shouldRefreshRelatedTracks) {
      setIsPrimaryTrackReady(false);
    }
    if (shouldRefreshRelatedTracks) {
      resetOverlayContext();
    }
    // --------- 1. Seleccionar archivo -----------------
    const selectedFile = eventFiles.find((f) => f.id === fileId) ?? null;
    if (!isTrackLoadActive(loadToken)) {
      return [];
    }
    const isSegmentSelection = segmentIndex !== undefined || rangeOverride !== undefined;
    setSelectedFile(selectedFile);
    setPrimarySelectionType(rangeOverride?.kind === 'start' ? 'start' : isSegmentSelection ? 'segment' : 'full');

    setHighlightSegments([]);

    setTacks([]);
    setStartLineSegments([]);

    setMarks([]);
    existingColorsReference.current = [];

    if (!selectedFile) {
      console.error('El archivo no está disponible.');

      clearLoadedTrackState();
      resetOverlayContext();
      return [];
    }

    // --------- 3. Obtener trackData (con caché) -----------------
    let trackDataArray: TrackPoint[];
    if (selectedFile.id === lastFileId && cachedTrackData.length > 0) {
      trackDataArray = cachedTrackData;
    } else {
      if (!trackDataCacheRef.current.has(fileId)) {
        setLoa(1);
      }
      const response = await fetchTrackData(fileId, { loadToken });
      if (!isTrackLoadActive(loadToken)) {
        return [];
      }
      if (!response.success) {
        console.error('Error fetching track data:', response);

        clearLoadedTrackState();
        resetOverlayContext();
        return [];
      }
      trackDataArray = response.data.trackDataArray;
      setCachedTrackData(trackDataArray);
      setLastFileId(fileId);
    }
    if ((selectedFile.autoName ?? false) && trackDataArray.length > 0) {
      const midPoint = trackDataArray[Math.floor(trackDataArray.length / 2)];
      const midDate = formatDateYMD(midPoint.utc ?? midPoint.date ?? null);
      if (midDate && midDate !== selectedFile.name) {
        setEventFiles((previous) =>
          previous.map((file) =>
            file.id === selectedFile.id ? { ...file, name: midDate, autoName: true } : file,
          ),
        );
        setSelectedFile((prev) =>
          prev && prev.id === selectedFile.id ? { ...prev, name: midDate, autoName: true } : prev,
        );
      }
    }

    // --------- 4. Calcular segmento y filtrar -----------------
    const segment =
      segmentIndex === undefined ? null : selectedFile.segments.find((s) => s.id === segmentIndex);
    const resolvedRangeOverride =
      rangeOverride?.kind === 'start' && segment
        ? {
            ...buildStartRangeForSegment(segment, trackDataArray),
            kind: 'start' as const,
          }
        : rangeOverride;

    const effectiveStartIndex = resolvedRangeOverride?.start ?? (segment ? segment.start_index : 0);
    const effectiveEndIndex =
      resolvedRangeOverride?.end ?? (segment ? segment.end_index : trackDataArray.length - 1);
    const clampedStart = Math.max(0, Math.min(trackDataArray.length - 1, effectiveStartIndex));
    const clampedEnd = Math.max(
      clampedStart,
      Math.min(trackDataArray.length - 1, effectiveEndIndex),
    );

    const filteredTrackData =
      segment == null && !rangeOverride
        ? trackDataArray
        : trackDataArray.slice(clampedStart, clampedEnd + 1);

    if (filteredTrackData.length === 0) {
      console.warn('filteredTrackData vacío. No se puede continuar.');

      clearLoadedTrackState();
      resetOverlayContext();
      return [];
    }

    // --------- 5. Calcular races y estados derivados ----------
    const startIndex = clampedStart;
    const selectedEndIndex = clampedEnd;

    const soloRaces =
      segment && segment.segment_type === true
        ? obtenerRaces(0, trackDataArray.length - 1, fileId, eventFiles, segmentIndex)
        : obtenerRaces(startIndex, selectedEndIndex, fileId, eventFiles);
    const shouldShowStartLine = shouldShowStartLineForSelection(
      segment,
      resolvedRangeOverride,
      selectedFile.modes,
    );
    const startLineVisibleRaces = shouldShowStartLine ? soloRaces : [];

    setRaces(soloRaces);
    setStartLineSegments(startLineVisibleRaces);
    const rangeAbs = { start: startIndex, end: selectedEndIndex };

    // ----------------------------------------------------------------
    // ───────── fetchTacks con rango (antes estaba sin segundo arg) ───
    await fetchTacks(fileId, rangeAbs, trackDataArray, loadToken); // ← cambio
    if (!isTrackLoadActive(loadToken)) {
      return [];
    }

    // --------- 6. Guardar trackData y trackModes en estado -----
    const firstPointUtc = filteredTrackData[0]?.utc;
    const lastPointUtc = filteredTrackData.at(-1)?.utc;
    const selectionRange =
      firstPointUtc && lastPointUtc ? { startUtc: firstPointUtc, endUtc: lastPointUtc } : null;

    const fullTrackStartUtc = trackDataArray[0]?.utc;
    const fullTrackEndUtc = trackDataArray.at(-1)?.utc;
    const fullTrackRange =
      fullTrackStartUtc && fullTrackEndUtc
        ? { startUtc: fullTrackStartUtc, endUtc: fullTrackEndUtc }
        : null;

    // Para entrenamientos buscamos solapes contra el rango completo del archivo (mayor probabilidad de solape)
    const searchRange =
      segment && segment.segment_type === false
        ? (fullTrackRange ?? selectionRange)
        : (selectionRange ?? fullTrackRange ?? null);
    console.debug('[relatedTracks] computed ranges', {
      fileId,
      segmentId: segment?.id,
      segmentType: segment?.segment_type,
      selectionRange,
      fullTrackRange,
      searchRange,
    });

    const nextTrackData = filteredTrackData === trackData ? [...filteredTrackData] : filteredTrackData;
    setPrimaryTimeRange(selectionRange);
    setOverlapSearchRange(searchRange ?? null);
    setTrackData(nextTrackData);
    setIsPrimaryTrackReady(true);
    setActiveSidebarSelection(
      resolvedRangeOverride && segment
        ? {
            type: 'leg',
            fileId,
            segmentId: segment.id,
            start: startIndex,
            end: selectedEndIndex,
            kind: resolvedRangeOverride.kind ?? 'leg',
          }
        : segment
          ? { type: 'segment', fileId, segmentId: segment.id }
          : { type: 'file', fileId },
    );

    return nextTrackData;
  };

  const ensureOverlayTrackLoaded = useCallback(
    async (fileId: number) => {
      if (overlayTracks[fileId]) {
        return;
      }
      const response = await fetchTrackData(fileId, { hydrateState: false });
      if (!response.success || response.data.trackDataArray.length === 0) {
        showMessage(
          'error',
          t(NOTIF_ERROR),
          t('race.relatedTracks.fetchError', 'Failed to load overlay track.'),
        );
        return;
      }
      setOverlayTracks((previous) => ({ ...previous, [fileId]: response.data.trackDataArray }));
    },
    [fetchTrackData, overlayTracks, t],
  );

  const assignOverlayColor = useCallback((key: string) => {
    setOverlayColors((previous) => {
      if (previous[key]) {
        return previous;
      }
      const usedColors = new Set(Object.values(previous));
      const paletteColor =
        RELATED_TRACK_COLORS.find((color) => !usedColors.has(color)) ??
        RELATED_TRACK_COLORS[(Object.keys(previous).length + 1) % RELATED_TRACK_COLORS.length];
      return { ...previous, [key]: paletteColor };
    });
  }, []);

  const toggleRelatedTrackSelection = useCallback(
    (key: string) => {
      setSelectedOverlayKeys((previous) => {
        if (previous.includes(key)) {
          return previous.filter((entry) => entry !== key);
        }
        const parsed = parseOverlayKey(key);
        if (parsed) {
          void ensureOverlayTrackLoaded(parsed.fileId);
        }
        assignOverlayColor(key);
        return [...previous, key];
      });
    },
    [assignOverlayColor, ensureOverlayTrackLoaded],
  );

  /**
   * Borra el mapa anterior (si existe) y dibuja uno nuevo con la trackData que reciba.
   */

  /**
   * Orquestador: primero carga/almacena datos, luego actualiza el mapa.
   */

  const addHighlight = useCallback(
    (startIndex: number, endIndex?: number, color?: string) => {
      setHighlightSegments((previous) => {
        // Convertir undefined a null
        const finalEndIndex = endIndex === undefined ? null : endIndex;
        // Si no se pasa color, generar uno aleatorio
        const finalColor = color ?? generateRandomColor();

        // Buscar segmento existente sin cierre
        const existingIndex = previous.findIndex(
          (seg) => seg.startIndex === startIndex && seg.endIndex === null,
        );

        if (existingIndex !== -1) {
          const updated = [...previous];
          updated[existingIndex] = {
            ...updated[existingIndex],
            endIndex: finalEndIndex,
            // Mantener el color original del segmento
            color: updated[existingIndex].color,
          };
          return updated;
        }

        // Añadir nuevo segmento con el color final
        return [
          ...previous,
          {
            id: createHighlightId(),
            startIndex,
            endIndex: finalEndIndex,
            color: finalColor,
          },
        ];
      });
      if (endIndex !== undefined) {
        removePreviewLayers();
        setDraftHighlight((current) =>
          current && (current.source === 'chart' || current.source === 'chartPreview')
            ? null
            : current,
        );
      }
    },
    [generateRandomColor, removePreviewLayers, setDraftHighlight],
  );
  // utils/map.ts

  // ⬇️ Sustituye COMPLETAMENTE la versión anterior
  const handleFileClickAsync = async (
    fileId: number,
    segmentIndex?: number,
    rangeOverride?: RangeOverride,
  ): Promise<{ trackData: TrackPoint[]; marks: Mark[] } | null> => {
    const shouldRemountMaps = lastFileId !== fileId;
    const loadToken = trackLoadTokenRef.current + 1;
    trackLoadTokenRef.current = loadToken;
    setIsTrackLoadPending(true);
    // ① Reset previo: limpiar highlights + preview + modal + editing state
    resetHighlights();
    setSelectedHighlightSegment(null);
    setShowSaveTrainingModal(false);
    existingColorsReference.current = [];
    if (shouldRemountMaps) {
      setLoaderKey(Date.now());
    }
    // Clear any active segment editing when loading a new file/segment
    setSegmentEditor(null);
    setSegmentEditorMode('idle');

    // debug: '[handleFileClickAsync]', { fileId, segmentIndex }

    try {
      // ② Carga de datos (mantiene trackData si es el mismo archivo)
      const trackData = await cargarDatosDeArchivo(fileId, segmentIndex, rangeOverride, loadToken);

      if (!isTrackLoadActive(loadToken)) {
        return null;
      }

      if (trackData.length === 0) {
        console.error('[handleFileClickAsync] TrackPoint vacío');
        return null;
      }

      let marksForSegment: Mark[] = [];
      // ③ Si es una carrera, trae también las marcas
      if (segmentIndex) {
        const marks_return = await fetchMarksByRaceId(segmentIndex);
        if (!isTrackLoadActive(loadToken)) {
          return null;
        }
        setMarks(marks_return);
        marksForSegment = marks_return;
      }
      return { trackData, marks: marksForSegment };
    } catch (error_) {
      if (isTrackLoadActive(loadToken)) {
        clearLoadedTrackState();
      }
      console.error('[handleFileClickAsync] unexpected error:', error_);
      return null;
    } finally {
      if (trackLoadTokenRef.current === loadToken) {
        setIsTrackLoadPending(false);
      }
    }
  };

  const clampEditingIndex = useCallback(
    (value: number) => {
      if (cachedTrackData.length === 0) {
        return Math.max(0, Math.floor(value));
      }
      const maxIndex = Math.max(0, cachedTrackData.length - 1);
      if (!Number.isFinite(value)) {
        return 0;
      }
      return Math.min(Math.max(0, Math.floor(value)), maxIndex);
    },
    [cachedTrackData.length],
  );

  const handleSegmentEditorMarksChange = (nextMarks: Mark[]) => {
    setMarks(nextMarks);
    setSegmentEditor((previous) => (previous ? { ...previous, marks: nextMarks } : previous));
  };

  const updateSegmentEditorBoundary = (boundary: 'start' | 'end', value: number) => {
    setSegmentEditor((previous) => {
      if (!previous) {
        return previous;
      }
      const clamped = clampEditingIndex(value);
      const nextRange =
        boundary === 'start'
          ? { start: clamped, end: previous.range.end }
          : { start: previous.range.start, end: clamped };
      return { ...previous, range: nextRange };
    });
  };

  const closeSegmentEditor = () => {
    if (segmentEditor) {
      setMarks(segmentEditor.originalMarks);
    }
    setSegmentEditor(null);
    setSegmentEditorMode('idle');
    setShowSidebar(true);
  };

  const startSegmentEditor = async (file: EventFile, segment: Segment) => {
    if (!requireOwnership(file.sharing)) {
      return;
    }
    let processId: string | null = null;
    try {
      processId = startProcess('generic', {
        label: t('race.segmentEditor.loading', 'Preparing segment editor'),
      });
    } catch {
      /* non-blocking */
    }
    try {
      const result = await handleFileClickAsync(file.id, segment.id);
      if (!result || result.trackData.length === 0) {
        throw new Error('Missing track data for editor');
      }
      const startIdx = clampEditingIndex(segment.start_index);
      const endIdx = clampEditingIndex(segment.end_index);
      setMarks(result.marks);
      setSegmentEditor({
        fileId: file.id,
        segment,
        range: { start: startIdx, end: endIdx },
        originalRange: { start: startIdx, end: endIdx },
        marks: result.marks,
        originalMarks: result.marks,
      });
      setSegmentEditorMode('idle');
    } catch (error) {
      console.error('Failed to open segment editor', error);
      showMessage('error', t(NOTIF_ERROR), getApiMessage(error, DEFAULT_API_ERROR_MSG));
    } finally {
      if (processId) {
        endProcess(processId);
      }
    }
  };

  const saveSegmentEditor = async () => {
    if (!segmentEditor) {
      return;
    }
    const token = localStorage.getItem('token');
    const rangeStart = Math.min(segmentEditor.range.start, segmentEditor.range.end);
    const rangeEnd = Math.max(segmentEditor.range.start, segmentEditor.range.end);
    let processId: string | null = null;
    try {
      processId = startProcess('generic', {
        label: t('race.segmentEditor.saving', 'Saving edits'),
      });
    } catch {
      /* ignore overlay errors */
    }
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL ?? ''}/api/track/saveMarks`,
        {
          segmentId: segmentEditor.segment.id,
          marks: segmentEditor.marks.map((mark) => ({ lat: mark.lat, lon: mark.lon })),
        },
        { headers: { Authorization: `Bearer ${token ?? ''}` } },
      );

      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL ?? ''}/api/track/updateSegmentRange`,
        {
          segmentId: segmentEditor.segment.id,
          startIndex: rangeStart,
          endIndex: rangeEnd,
        },
        { headers: { Authorization: `Bearer ${token ?? ''}` } },
      );

      setEventFiles((previous) =>
        previous.map((file) =>
          file.id === segmentEditor.fileId
            ? {
                ...file,
                segments: file.segments.map((seg) =>
                  seg.id === segmentEditor.segment.id
                    ? { ...seg, start_index: rangeStart, end_index: rangeEnd }
                    : seg,
                ),
              }
            : file,
        ),
      );
      setSelectedFile((previous) => {
        if (!previous || previous.id !== segmentEditor.fileId) {
          return previous;
        }
        return {
          ...previous,
          segments: previous.segments.map((seg) =>
            seg.id === segmentEditor.segment.id
              ? { ...seg, start_index: rangeStart, end_index: rangeEnd }
              : seg,
          ),
        };
      });
      setSegmentEditor(null);
      setSegmentEditorMode('idle');
      setShowSidebar(true);
      showMessage(
        'success',
        t('common.success', 'Success'),
        t('race.segmentEditor.saved', 'Segment updated'),
      );
      await handleFileClickAsync(segmentEditor.fileId, segmentEditor.segment.id);
    } catch (error) {
      console.error('Failed to save segment edits', error);
      showMessage('error', t(NOTIF_ERROR), getApiMessage(error, DEFAULT_API_ERROR_MSG));
    } finally {
      if (processId) {
        endProcess(processId);
      }
    }
  };

  useEffect(() => {
    // Solo limpiar highlights cuando cambiamos de archivo (lastFileId)
    resetHighlights();
    setSelectedHighlightSegment(null);
    setShowSaveTrainingModal(false);
    existingColorsReference.current = [];
    setLoaderKey(Date.now());
  }, [trackData]);

  useEffect(() => {
    if (segmentEditor && selectedFile && selectedFile.id !== segmentEditor.fileId) {
      setSegmentEditor(null);
      setSegmentEditorMode('idle');
    }
  }, [segmentEditor, selectedFile]);

  useEffect(() => {
    if (!segmentEditor || cachedTrackData.length === 0) {
      return;
    }
    setSegmentEditor((previous) => {
      if (!previous) {
        return previous;
      }
      const nextStart = clampEditingIndex(previous.range.start);
      const nextEnd = clampEditingIndex(previous.range.end);
      if (nextStart === previous.range.start && nextEnd === previous.range.end) {
        return previous;
      }
      return {
        ...previous,
        range: { start: nextStart, end: nextEnd },
      };
    });
  }, [cachedTrackData.length, clampEditingIndex, segmentEditor]);

  const toggleAllSegments = () => {
    setShowAllSegments((previous) => {
      const nextValue = !previous;

      setExpandedFiles((previousExpanded) => {
        if (nextValue) {
          const updatedExpanded = { ...previousExpanded };

          for (const file of eventFiles) {
            if (file.segments && file.segments.length > 0) {
              updatedExpanded[file.id] = true;
            }
          }

          return updatedExpanded;
        }

        return {};
      });

      return nextValue;
    });
  };

  const saveHighlightAsTraining = (segment: HighlightSegment) => {
    const targetFile = selectedFile ?? findFileById(lastFileId);
    if (!requireOwnership(targetFile?.sharing)) {
      return;
    }
    // Abre el modal para que el usuario especifique el nombre del highlight
    setSelectedHighlightSegment(segment);
    setShowSaveTrainingModal(true);
  };
  const handleConfirmSaveHighlight = async (
    highlightName: string,
    asRace: boolean,
  ): Promise<void> => {
    try {
      if (!selectedHighlightSegment) {
        return;
      }
      const targetFile = selectedFile ?? findFileById(lastFileId);
      if (!requireOwnership(targetFile?.sharing)) {
        setShowSaveTrainingModal(false);
        setSelectedHighlightSegment(null);
        return;
      }
      const segment = selectedHighlightSegment;
      const [startIndex, endIndex] =
        segment.startIndex < segment.endIndex!
          ? [segment.startIndex, segment.endIndex!]
          : [segment.endIndex!, segment.startIndex];
      const originalStartIndex = trackData[startIndex].globalIndex ?? startIndex;
      const originalEndIndex = trackData[endIndex].globalIndex ?? endIndex;
      type AddSegmentResponse = { success: boolean; data: Segment; message?: string };
      const { data } = await axios.post<AddSegmentResponse>(
        `${process.env.REACT_APP_API_BASE_URL ?? ''}/api/track/addSegment`,
        {
          fileId: lastFileId,
          segmentType: asRace,
          startIndex: originalStartIndex,
          endIndex: originalEndIndex,
          name: highlightName,
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token') ?? ''}` },
        },
      );
      if (data.success) {
        const newSegment = data.data;
        setEventFiles((previous) =>
          previous.map((file) =>
            file.id === lastFileId
              ? {
                  ...file,
                  segments: [...file.segments, newSegment].sort(
                    (a: Segment, b: Segment) => a.start_index - b.start_index,
                  ),
                }
              : file,
          ),
        );
        setHighlightSegments((previous) =>
          previous.map((h) =>
            h.startIndex === segment.startIndex && h.endIndex === segment.endIndex
              ? { ...h, isSaved: true }
              : h,
          ),
        );
      } else {
        // Show error message if available
        const apiMessage = typeof data?.message === 'string' ? data.message : DEFAULT_API_ERROR_MSG;
        showMessage('error', 'Error', apiMessage);
      }
    } catch (error) {
      showMessage('error', 'Error', getApiMessage(error, DEFAULT_API_ERROR_MSG));
    }
    setShowSaveTrainingModal(false);
    setSelectedHighlightSegment(null);
  };
  const handleGenerateReport = async (fileId: number, raceId?: number): Promise<void> => {
    let processId: string | undefined;
    try {
      // Build user-friendly context: Event name · File date · Race name (if any)
      const fileObject = eventFiles.find((f) => f.id === fileId);
      if (!requireOwnership(fileObject?.sharing)) {
        return;
      }
      let raceLabel = '';
      if (typeof raceId === 'number' && fileObject?.segments) {
        const raceSeg = fileObject.segments.find((s) => s.id === raceId);
        if (raceSeg) {
          if (raceSeg.name && raceSeg.name.trim().length > 0) {
            raceLabel = raceSeg.name.trim();
          } else {
            const raceList = fileObject.segments.filter((s) => s.segment_type);
            const index = raceList.findIndex((s) => s.id === raceId);
            raceLabel = index >= 0 ? `Race ${index + 1}` : 'Race';
          }
        }
      }
      const parts: string[] = [];
      if (selectedEventName && selectedEventName.trim().length > 0) {
        parts.push(selectedEventName);
      }
      if (fileObject?.name) {
        parts.push(fileObject.name);
      }
      if (raceLabel) {
        parts.push(raceLabel);
      }
      const friendly = parts.join(' · ');

      processId = startProcess('generate', {
        label: 'Generando informe',
        sublabel: friendly,
        progress: 5,
      });
      const token = localStorage.getItem('token');

      const response = await axios.post<{ success: boolean; message?: string; jobId?: string }>(
        `${process.env.REACT_APP_API_BASE_URL ?? ''}/api/user/GenerateReport`,
        {
          fileId,
          raceIds: raceId === undefined ? [] : [raceId],
        },
        { headers: { Authorization: `Bearer ${token ?? ''}` } },
      );

      if (response.data?.success || response.data?.jobId) {
        if (!response.data.jobId || !token) {
          throw new Error('The server did not return a report job id');
        }
        await pollUploadJob({
          baseUrl: process.env.REACT_APP_API_BASE_URL ?? '',
          token,
          jobId: response.data.jobId,
          onUpdate: (job) => {
            if (processId) {
              updateProcess(processId, {
                label: job.message || 'Generando informe',
                sublabel: friendly,
                progress: job.progress,
              });
            }
          },
        });
        showMessage('success', t('notifications.success'), t('reports.generatedSuccess'));
        if (window.location.href.includes('/reports')) {
          window.dispatchEvent(new CustomEvent('reports:refresh'));
        }
      } else {
        const apiMessage =
          typeof response.data?.message === 'string'
            ? response.data.message
            : t('reports.fetchFail');
        showMessage('error', t(NOTIF_ERROR), apiMessage);
      }
    } catch (error) {
      showMessage('error', t(NOTIF_ERROR), getApiMessage(error, DEFAULT_API_ERROR_MSG));
    } finally {
      if (processId) {
        endProcess(processId);
      }
    }
  };

  const handleRenameSegment = async (
    segmentId: number,
    _newIgnored?: string | null | undefined,
  ): Promise<void> => {
    const trimmed = editingSegmentText.trim();
    const parentFile = eventFiles.find((file) => file.segments.some((s) => s.id === segmentId));
    if (!requireOwnership(parentFile?.sharing)) {
      setEditingSegmentId(null);
      setEditingSegmentText('');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL ?? ''}/api/user/renameFileDataSegment`,
        { segmentId: segmentId, newName: trimmed.length === 0 ? null : trimmed },
        { headers: { Authorization: `Bearer ${token ?? ''}` } },
      );

      // Update local state so the sidebar reflects the new name
      setEventFiles((previous) =>
        previous.map((file) => ({
          ...file,
          segments: file.segments.map((s) =>
            s.id === segmentId ? { ...s, name: trimmed.length === 0 ? null : trimmed } : s,
          ),
        })),
      );
    } catch (error) {
      showMessage('error', 'Error', getApiMessage(error, DEFAULT_API_ERROR_MSG));
    } finally {
      setEditingSegmentId(null);
      setEditingSegmentText('');
    }
  };

  const handleRenameFile = async (fileId: number): Promise<void> => {
    const targetFile = eventFiles.find((f) => f.id === fileId);
    if (!requireOwnership(targetFile?.sharing)) {
      setEditingFileId(null);
      setEditingFileText('');
      return;
    }
    const currentName = targetFile?.name ?? '';
    const trimmed = editingFileText.trim();
    const newNamePayload = trimmed.length === 0 ? null : trimmed;
    if (newNamePayload === currentName || (newNamePayload === null && currentName === '')) {
      setEditingFileId(null);
      return;
    }

    const fallbackName =
      targetFile?.date && !Number.isNaN(new Date(targetFile.date).getTime())
        ? new Date(targetFile.date).toLocaleDateString(undefined, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          })
        : currentName || 'File';
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL ?? ''}/api/user/renameCsvFile`,
        { fileId, newName: newNamePayload },
        { headers: { Authorization: `Bearer ${token ?? ''}` } },
      );
      const nextName = newNamePayload ?? fallbackName;
      setEventFiles((previous) =>
        previous.map((f) => (f.id === fileId ? { ...f, name: nextName, autoName: false } : f)),
      );
      setSelectedFile((prev) =>
        prev && prev.id === fileId ? { ...prev, name: nextName, autoName: false } : prev,
      );
    } catch (error) {
      showMessage('error', 'Error', getApiMessage(error, DEFAULT_API_ERROR_MSG));
    } finally {
      setEditingFileId(null);
      setEditingFileText('');
    }
  };

  const handleDeleteSegment = async (segmentId: number) => {
    const parentFile = eventFiles.find((file) =>
      file.segments.some((segment) => segment.id === segmentId),
    );
    if (!requireOwnership(parentFile?.sharing)) {
      return;
    }
    try {
      interface DeleteSegmentResponse {
        success: boolean;
        message?: string;
      }
      const { data } = await axios.post<DeleteSegmentResponse>(
        `${process.env.REACT_APP_API_BASE_URL ?? ''}/api/track/deleteSegment`,
        { segmentId },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token') ?? ''}` },
        },
      );

      if (data.success) {
        setEventFiles((previousEventFiles) =>
          previousEventFiles.map((file) => ({
            ...file,
            segments: file.segments.filter((segment) => segment.id !== segmentId),
          })),
        );
        setSelectedFile((previousSelected) =>
          previousSelected && previousSelected.id === parentFile?.id
            ? {
                ...previousSelected,
                segments: previousSelected.segments.filter((segment) => segment.id !== segmentId),
              }
            : previousSelected,
        );
        setRaces((previousRaces) =>
          previousRaces.some((race) => race.id === segmentId)
            ? previousRaces.filter((race) => race.id !== segmentId)
            : previousRaces,
        );
        if (editingSegmentId === segmentId) {
          setEditingSegmentId(null);
          setEditingSegmentText('');
        }
        setActiveSidebarSelection((currentSelection) =>
          currentSelection?.type !== 'file' && currentSelection?.segmentId === segmentId
            ? null
            : currentSelection,
        );
      } else {
        const apiMessage =
          typeof data?.message === 'string' ? data.message : 'Failed to delete segment';
        showMessage('error', 'Error', apiMessage);
      }
    } catch (error) {
      showMessage('error', 'Error', getApiMessage(error, DEFAULT_API_ERROR_MSG));
    }
  };

  const colorSchemes = {
    speed: { start: '#D3D3D3', end: '#00009B' }, // Ajusta los colores para el modo "speed"
    tactical: { start: '#C80000', mid: '#C8C8C8', end: '#00C800' }, // Rojo a blanco a verde para táctico
  };

  const isEventSelected = selectedEventName && selectedEventName !== EVENT_PLACEHOLDER;
  const isDateSelected = selectedDate && selectedDate !== DATE_PLACEHOLDER;

  const shouldShowEmptyMessage = !isEventSelected && !isDateSelected;

  //Logica añadida por dennis, movimiento y arrastre de race stats panel
  // (Ejemplo de uso: handler para iniciar drag, donde se registra startPos)
  // let startPos = 0;
  // document.addEventListener('mousedown', (e) => {
  //     startPos = direction === "vertical" ? e.clientY : e.clientX;
  // });
  // (En tu código real, busca la asignación de startPos y modifica así:)
  // startPos = direction === "vertical" ? e.clientY : e.clientX;

  useEffect(() => {
    const fetchUserBoats = async () => {
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.post<{ success: boolean; userBoats: Boat[] }>(
          `${process.env.REACT_APP_API_BASE_URL ?? ''}/api/user/getUserBoats`,
          {},
          { headers: { Authorization: `Bearer ${token ?? ''}` } },
        );
        if (data.success) {
          setUserBoats(data.userBoats);
        } else {
          setUserBoats([]);
        }
      } catch (error) {
        console.error('Error fetching user boats:', error);
        setUserBoats([]);
      }
    };
    void fetchUserBoats();
  }, []);

  const highlightTrackSegment = useCallback(
    (_segKey: string, _segment: HighlightSegment, _data: TrackPoint[]) => {
      // El render real de subrayados vive en la source/layer única de abajo.
    },
    [],
  );

  /* Helper: decide layout a partir de los paneles activos */

  useEffect(() => {
    setLayoutMode(decideLayout(panel1Mode, panel2Mode, panel3Mode, panel4Mode));
  }, [panel1Mode, panel2Mode, panel3Mode, panel4Mode]);

  function removeHighlight(index: number) {
    setHighlightSegments((previous) => previous.filter((_, index_) => index_ !== index));
  }

  const updateHighlight = useCallback(
    (index: number, startIndex: number, endIndex: number, color: string) => {
      if (!Number.isFinite(startIndex) || !Number.isFinite(endIndex)) {
        return;
      }
      setHighlightSegments((previous) =>
        previous.map((seg, index_) =>
          index_ === index ? { ...seg, startIndex, endIndex, color } : seg,
        ),
      );
    },
    [],
  );
  useEffect(() => {
    const geo = buildHighlightFeatureCollection(highlightSegments, draftHighlight, trackData);
    for (const { map, prefix } of getActiveMaps()) {
      const addOrUpdate = () => renderHighlightCollectionOnMap(map, prefix, geo);
      map.isStyleLoaded() ? addOrUpdate() : map.once('idle', addOrUpdate);
    }
  }, [
    draftHighlight,
    effectiveLayout,
    getActiveMaps,
    highlightSegments,
    renderHighlightCollectionOnMap,
    trackData,
  ]);

  useEffect(() => {
    if (
      !draftHighlight ||
      (draftHighlight.source !== 'chart' && draftHighlight.source !== 'map') ||
      draftHighlight.endIndex === null
    ) {
      return;
    }
    const draftStart = Math.min(draftHighlight.startIndex, draftHighlight.endIndex);
    const draftEnd = Math.max(draftHighlight.startIndex, draftHighlight.endIndex);
    const hasMatchingSegment = highlightSegments.some((segment) => {
      if (segment.endIndex == null) {
        return false;
      }
      const segStart = Math.min(segment.startIndex, segment.endIndex);
      const segEnd = Math.max(segment.startIndex, segment.endIndex);
      return segStart === draftStart && segEnd === draftEnd;
    });
    if (!hasMatchingSegment) {
      return;
    }
    removePreviewLayers();
    setDraftHighlight((current) =>
      current && (current.source === 'chart' || current.source === 'map') ? null : current,
    );
  }, [draftHighlight, highlightSegments, removePreviewLayers, setDraftHighlight]);

  const relatedFilesById = useMemo(() => {
    const dictionary: Record<number, RelatedTrackFile> = {};
    for (const file of relatedFileCandidates) {
      dictionary[file.fileId] = file;
    }
    return dictionary;
  }, [relatedFileCandidates]);

  const getRelatedTrackName = useCallback(
    (fileId: number): string => {
      const metadata = relatedFilesById[fileId];
      if (metadata) {
        const boat = metadata.boatName?.trim();
        if (boat && boat.length > 0) {
          return boat;
        }
        if (metadata.eventName && metadata.eventName.trim().length > 0) {
          return metadata.eventName.trim();
        }
      }
      return t('race.relatedTracks.fallbackName', {
        id: fileId,
        defaultValue: `Boat ${fileId}`,
      });
    },
    [relatedFilesById, t],
  );

  const formatSegmentSidebarLabel = useCallback(
    (fileId: number, segment?: Segment): string | null => {
      if (!segment) {
        return null;
      }
      if (segment.name && segment.name.trim().length > 0) {
        return segment.name.trim();
      }
      const fileSegments = relatedFileSegments[fileId];
      if (segment.segment_type) {
        const raceSegments = fileSegments?.filter((s) => s.segment_type) ?? [];
        const raceIndex = raceSegments.findIndex((race) => race.id === segment.id);
        const position = raceIndex >= 0 ? raceIndex + 1 : undefined;
        return t(RACE_NUMBER_KEY, {
          n: position ?? segment.id,
          defaultValue: `Race ${position ?? segment.id}`,
        });
      }
      return `Training ${segment.id}`;
    },
    [relatedFileSegments, t],
  );

  const buildOverlayOptionLabel = useCallback(
    (fileId: number, segment?: Segment): string => {
      const boatName = getRelatedTrackName(fileId);
      const metadata = relatedFilesById[fileId];
      const descriptorParts: string[] = [];
      if (metadata?.eventName && metadata.eventName.trim().length > 0) {
        descriptorParts.push(metadata.eventName.trim());
      }
      const fileLabel =
        typeof metadata?.fileName === 'string' && metadata.fileName.trim().length > 0
          ? metadata.fileName.trim()
          : null;
      if (fileLabel) {
        descriptorParts.push(fileLabel);
      } else {
        const dateLabel = formatOverlayDateLabel(metadata?.fileDate);
        if (dateLabel) {
          descriptorParts.push(dateLabel);
        } else {
          const timeLabel = formatOverlayTimeRange(metadata?.startTime, metadata?.endTime);
          if (timeLabel) {
            descriptorParts.push(timeLabel);
          }
        }
      }
      const segmentLabel = formatSegmentSidebarLabel(fileId, segment);
      if (segmentLabel) {
        descriptorParts.push(segmentLabel);
      }
      const descriptor = descriptorParts.join(' · ');
      return descriptor.length > 0 ? `${boatName} · ${descriptor}` : boatName;
    },
    [formatSegmentSidebarLabel, getRelatedTrackName, relatedFilesById],
  );

  const doesSegmentOverlapPrimaryRange = useCallback(
    (segment?: Segment | null): boolean => {
      if (!primaryRangeBounds || !segment?.start_utc) {
        return true;
      }
      const endUtc = segment.end_utc ?? segment.start_utc;
      const segmentStartMs = new Date(segment.start_utc).getTime();
      const segmentEndMs = new Date(endUtc).getTime();
      if (Number.isNaN(segmentStartMs) || Number.isNaN(segmentEndMs)) {
        return true;
      }
      const normalizedStart = Math.min(segmentStartMs, segmentEndMs);
      const normalizedEnd = Math.max(segmentStartMs, segmentEndMs);
      return (
        normalizedEnd >= primaryRangeBounds.startMs && normalizedStart <= primaryRangeBounds.endMs
      );
    },
    [primaryRangeBounds],
  );

  type OverlayOption = {
    key: string;
    fileId: number;
    label: string;
    kind: OverlayKeyKind;
    segment?: Segment;
  };

  const overlayFullOptions = useMemo<OverlayOption[]>(
    () =>
      relatedFileCandidates.map((file) => ({
        key: buildFullOverlayKey(file.fileId),
        fileId: file.fileId,
        label: buildOverlayOptionLabel(file.fileId),
        kind: 'full' as const,
      })),
    [relatedFileCandidates, buildOverlayOptionLabel],
  );

  const overlaySegmentOptions = useMemo<OverlayOption[]>(() => {
    return relatedFileCandidates.flatMap((file) => {
      const segments = relatedFileSegments[file.fileId];
      if (!segments || segments.length === 0) {
        return [];
      }
      const filteredSegments = segments.filter((segment) =>
        doesSegmentOverlapPrimaryRange(segment),
      );
      if (filteredSegments.length === 0) {
        return [];
      }
      return filteredSegments.map((segment) => ({
        key: buildSegmentOverlayKey(file.fileId, segment.id),
        fileId: file.fileId,
        label: buildOverlayOptionLabel(file.fileId, segment),
        kind: 'segment' as const,
        segment,
      }));
    });
  }, [
    buildOverlayOptionLabel,
    doesSegmentOverlapPrimaryRange,
    relatedFileCandidates,
    relatedFileSegments,
  ]);

  const overlayOptionsForView =
    primarySelectionType === 'segment' && overlaySegmentOptions.length > 0
      ? overlaySegmentOptions
      : overlayFullOptions;

  const buildLegOptionsForSegment = useCallback(
    (segment: Segment | null | undefined, modes: EventFile['modes'] | undefined) => {
      if (!segment) {
        return [];
      }
      const startRange = buildStartRangeForSegment(segment);
      const startOptions =
        segment.segment_type === true
          ? [
              {
                id: `start-${segment.id}`,
                kind: 'start' as const,
                label: t('race.startSegment', 'Start'),
                start: startRange.start,
                end: startRange.end,
              },
            ]
          : [];
      if (!modes || modes.length === 0) {
        return startOptions;
      }
      const legs = modes.filter(
        (mode) => mode.start_index <= segment.end_index && mode.end_index >= segment.start_index,
      );
      let upCounter = 1;
      let downCounter = 1;
      return [
        ...startOptions,
        ...legs.map((mode, index) => {
          const isUpwind = index % 2 === 0;
          const label = isUpwind
            ? t('map.raceStatsPanel.table.upMode', {
                mode: upCounter,
                defaultValue: `Ceñida ${upCounter}`,
              })
            : t('map.raceStatsPanel.table.downMode', {
                mode: downCounter,
                defaultValue: `Popa ${downCounter}`,
              });
          if (isUpwind) {
            upCounter += 1;
          } else {
            downCounter += 1;
          }
          return {
            id: mode.id ?? index,
            kind: 'leg' as const,
            label,
            start: Math.max(mode.start_index, segment.start_index),
            end: Math.min(mode.end_index, segment.end_index),
          };
        }),
      ];
    },
    [t],
  );

  const isSidebarFileActive = useCallback(
    (fileId: number) =>
      activeSidebarSelection?.type === 'file' && activeSidebarSelection.fileId === fileId,
    [activeSidebarSelection],
  );

  const isSidebarFileActiveParent = useCallback(
    (fileId: number) =>
      activeSidebarSelection !== null &&
      activeSidebarSelection.fileId === fileId &&
      activeSidebarSelection.type !== 'file',
    [activeSidebarSelection],
  );

  const isSidebarSegmentActive = useCallback(
    (fileId: number, segmentId: number) =>
      activeSidebarSelection?.type === 'segment' &&
      activeSidebarSelection.fileId === fileId &&
      activeSidebarSelection.segmentId === segmentId,
    [activeSidebarSelection],
  );

  const isSidebarSegmentActiveParent = useCallback(
    (fileId: number, segmentId: number) =>
      activeSidebarSelection?.type === 'leg' &&
      activeSidebarSelection.fileId === fileId &&
      activeSidebarSelection.segmentId === segmentId,
    [activeSidebarSelection],
  );

  const isSidebarLegActive = useCallback(
    (fileId: number, segmentId: number, leg: { start: number; end: number; kind: RangeSelectionKind }) => {
      if (
        activeSidebarSelection?.type !== 'leg' ||
        activeSidebarSelection.fileId !== fileId ||
        activeSidebarSelection.segmentId !== segmentId ||
        activeSidebarSelection.kind !== leg.kind
      ) {
        return false;
      }
      if (leg.kind === 'start') {
        return true;
      }
      return activeSidebarSelection.start === leg.start && activeSidebarSelection.end === leg.end;
    },
    [activeSidebarSelection],
  );

  const overlayOptionLookup = useMemo<Record<string, OverlayOption>>(() => {
    const map: Record<string, OverlayOption> = {};
    for (const option of overlayFullOptions) {
      map[option.key] = option;
    }
    for (const option of overlaySegmentOptions) {
      map[option.key] = option;
    }
    return map;
  }, [overlayFullOptions, overlaySegmentOptions]);

  useEffect(() => {
    setSelectedOverlayKeys((previous) => previous.filter((key) => overlayOptionLookup[key]));
  }, [overlayOptionLookup]);

  const overlayLayers = useMemo<OverlayTrackLayer[]>(() => {
    const layers: OverlayTrackLayer[] = [];
    for (const key of selectedOverlayKeys) {
      const option = overlayOptionLookup[key];
      if (!option) {
        continue;
      }
      const track = overlayTracks[option.fileId];
      const color = overlayColors[key];
      if (!track || track.length === 0 || !color) {
        continue;
      }
      let trackData = track;
      if (option.kind === 'segment' && option.segment) {
        const startIndex = Math.max(0, option.segment.start_index);
        const endIndex = Math.min(track.length - 1, option.segment.end_index);
        if (startIndex <= endIndex) {
          trackData = track.slice(startIndex, endIndex + 1);
        }
      }

      if (primaryRangeBounds) {
        const filteredByRange = trackData.filter((point) => {
          if (!point?.utc) {
            return false;
          }
          const pointTime = new Date(point.utc).getTime();
          if (Number.isNaN(pointTime)) {
            return false;
          }
          return pointTime >= primaryRangeBounds.startMs && pointTime <= primaryRangeBounds.endMs;
        });
        trackData = filteredByRange;
      }

      if (trackData.length === 0) {
        continue;
      }

      layers.push({
        fileId: option.fileId,
        label: option.label,
        color,
        trackData,
        selectionKey: key,
      });
    }
    return layers;
  }, [primaryRangeBounds, selectedOverlayKeys, overlayTracks, overlayColors, overlayOptionLookup]);

  const overlayButtonLabel = useMemo(
    () => t('race.relatedTracks.triggerButton', 'Add more boats'),
    [t],
  );

  useEffect(() => {
    if (!isPrimaryTrackReady || lastFileId == null || !overlapSearchRange) {
      return;
    }
    void refreshRelatedTrackFiles(lastFileId, overlapSearchRange);
  }, [isPrimaryTrackReady, lastFileId, overlapSearchRange, refreshRelatedTrackFiles]);

  useEffect(() => {
    if (primarySelectionType !== 'segment') {
      return;
    }
    const missingFileIds = relatedFileCandidates
      .map((file) => file.fileId)
      .filter((fileId) => relatedFileSegments[fileId] === undefined);
    if (missingFileIds.length === 0) {
      return;
    }
    const token = localStorage.getItem('token');
    const fetchSegments = async () => {
      await Promise.all(
        missingFileIds.map(async (fileId) => {
          try {
            const { data } = await axios.post<{ success: boolean; data: Segment[] }>(
              `${process.env.REACT_APP_API_BASE_URL ?? ''}/api/track/getSegments`,
              { fileId },
              { headers: { Authorization: `Bearer ${token ?? ''}` } },
            );
            if (data.success && Array.isArray(data.data)) {
              setRelatedFileSegments((previous) => ({ ...previous, [fileId]: data.data }));
            } else {
              setRelatedFileSegments((previous) => ({ ...previous, [fileId]: [] }));
            }
          } catch (error) {
            console.error('Error fetching segments for related file', fileId, error);
            setRelatedFileSegments((previous) => ({ ...previous, [fileId]: [] }));
          }
        }),
      );
    };
    void fetchSegments();
  }, [primarySelectionType, relatedFileCandidates, relatedFileSegments]);

  useEffect(() => {
    setIsRelatedTracksModalOpen(false);
  }, [selectedFile?.id]);

  useEffect(() => {
    if (!isPrimaryTrackReady) {
      setIsRelatedTracksModalOpen(false);
    }
  }, [isPrimaryTrackReady]);

  useEffect(() => {
    setSelectedOverlayKeys([]);
  }, [primarySelectionType]);

  const segmentEditorFile =
    segmentEditor && selectedFile?.id === segmentEditor.fileId
      ? selectedFile
      : segmentEditor
        ? eventFiles.find((file) => file.id === segmentEditor.fileId) ?? null
        : null;
  const segmentEditorSegmentName = segmentEditor
    ? getSidebarSegmentDisplayName(segmentEditor.segment, segmentEditor.fileId)
    : null;

  // 2) Props comunes para ambos panels (líneas ~4360)
  // RaceInsightsPage.tsx  (reemplaza el bloque completo)
  const sidePanelCommonProperties: Omit<
    SidePanelPageProperties,
    'mode' | 'mapInstance' | 'mapContainer' | 'side' | 'panelId' | 'layout'
  > = {
    trackData,
    highlightSegments,
    saveHighlightAsTraining,
    addHighlight,

    generateRandomColor,

    tacks,

    removeHighlight, // ⬅️  nueva
    updateHighlight, // ⬅️  nueva
    draftHighlight,
    setDraftHighlight,

    isVisible: showSidePage, // ⬅️  nueva
    onClose: () => setShowSidePage(false), // ⬅️  nueva
    isTrackLoading: isTrackLoadPending,

    loaderKey,
    setSelectedHighlightSegment,
    setShowSaveTrainingModal,

    metricBounds,
    colorSchemes,

    races,
    startLineSegments,

    RenderizarRaces,
    cachedTrackData,
    selectedFile,
    primarySelectionType,

    showSidePage,
    setShowSidePage,
    showSidebar,
    setShowSidebar,

    globalToRelativeIndexMap,
    cachedTacksGeoJSON,
    marks,
    editingSegmentId: segmentEditor?.segment.id ?? null,
    editingRange: segmentEditor?.range ?? null,
    editingMode: segmentEditorMode,
    onEditingMarksChange: segmentEditor ? handleSegmentEditorMarksChange : undefined,
    onEditingRangeChange: segmentEditor ? updateSegmentEditorBoundary : undefined,
    onSaveEditing: segmentEditor ? saveSegmentEditor : undefined,
    onCancelEditing: segmentEditor ? closeSegmentEditor : undefined,
    editingSegmentType: segmentEditor?.segment.segment_type ? 'race' : 'training',
    editingSegmentName: segmentEditor
      ? getSidebarSegmentDisplayName(segmentEditor.segment, segmentEditor.fileId)
      : null,
    setEditingMode: segmentEditor ? setSegmentEditorMode : undefined,
    loa,
    setHighlightSegments,
    setCachedTacksGeoJSON,
    combineSegments,
    getTackColor,
    lastFileId,
    setLoaderKey,
    highlightTrackSegment,
    relatedTrackLayers: overlayLayers,
    sharedCameraRef: sharedMapCameraRef,
    mapInstance1,
    mapInstance2,
    mapInstance3,
    mapInstance4,
    primaryBoatName: selectedBoatId === null ? null : selectedBoatName,
    canManageSelectedFile,
    // ⬇️ Nuevo paquete polares para el SidePanel
    polars: {
      angles: polarsAngles,
      tws: polarsTws,
      speeds: polarsSpeeds,
      stds: polarsStds,
      raw: polarsTxt,
    },
  };

  //HTML de los objetos creados
  return (
    <div className="race-insights-container">
      {showSidebar && (
        <div className="sidebar-race">
          <button onClick={handleLogoClick} className="logo-button-race">
            <div className="logo-container-race">
              <SailTrackLogo
                className="logo-race"
                role="img"
                aria-label="SailTrack Logo"
                focusable="false"
              />
              <span className="logo-title-race">SailTrack</span>
            </div>
          </button>

          {/* Dropdown para Boat */}
          <div className="dropdown-container" ref={boatSelectReference}>
            <EnhancedSelect
              options={userBoats.map((boat) => {
                const shareTag = sharingLabel(boat.sharing);
                return {
                  value: boat.name,
                  label: shareTag ? `${boat.name} · ${shareTag}` : boat.name,
                };
              })}
              placeholder={t(KEY_PLACEHOLDER_BOAT)}
              value={selectedBoatName}
              onChange={handleBoatChange}
              className="dropdown-boat"
              searchable
              clearable
            />
          </div>

          {userBoats.length > 0 && selectedBoatId !== null && (
            <div
              className={`dropdown-container ${isBoatDropdownOpen ? 'shifted' : ''}`}
              ref={eventSelectReference}
            >
              <EnhancedSelect
                options={[...events].reverse().map((event) => {
                  const shareTag = sharingLabel(event.sharing);
                  const dateLabel =
                    event.event_date && !Number.isNaN(new Date(event.event_date).getTime())
                      ? new Date(event.event_date).toLocaleDateString()
                      : '';
                  const baseLabel = `${event.event_name}${dateLabel ? ' - ' + dateLabel : ''}`;
                  return {
                    value: String(event.id),
                    label: shareTag ? `${baseLabel} · ${shareTag}` : baseLabel,
                  };
                })}
                placeholder={t(KEY_PLACEHOLDER_EVENT)}
                value={selectedEventId === null ? EVENT_PLACEHOLDER : String(selectedEventId)}
                onChange={handleEventChange}
                className="dropdown-date"
                searchable
                clearable
              />
            </div>
          )}
          <div className="files-list">
            {eventFiles.length > 0 && (
              <div>
                <div className="files-title-container">
                  <strong>{t('files.title', 'Files')}</strong>
                </div>
                <div className="line-race-files" />
                <div className="show-hide-container">
                  <button
                    onClick={toggleAllSegments}
                    className={`show-hide-button ${showAllSegments ? 'show-all' : ''}`}
                  >
                    {showAllSegments
                      ? t('race.hideAllSegments', 'Hide All Races & Trainings')
                      : t('race.showAllSegments', 'Show All Races & Trainings')}
                    <ArrowRightIcon className="arrow-icon" />
                  </button>
                </div>
                <div className="container-file-items">
                  {eventFiles.map((file) => {
                    const canManageFile = canManageSharing(file.sharing);
                    const fileButtonGroupClass = canManageFile
                      ? 'file-item-button-group--own'
                      : 'file-item-button-group--shared';
                    const fileButtonClass = canManageFile
                      ? 'file-item-button--own'
                      : 'file-item-button--shared';
                    const isActiveFile = isSidebarFileActive(file.id);
                    const isActiveFileParent = isSidebarFileActiveParent(file.id);
                    const fileLabel =
                      (file.name ?? '').trim().length > 0
                        ? (file.name as string)
                        : t('common.file', 'File');
                    const hasSegments = Array.isArray(file.segments) && file.segments.length > 0;
                    const isExpanded = Boolean(expandedFiles[file.id]);
                    const toggleSegmentsLabel = hasSegments
                      ? isExpanded
                        ? t('race.toggleSegments.hide', {
                            name: fileLabel,
                            defaultValue: `Hide segments for ${fileLabel}`,
                          })
                        : t('race.toggleSegments.show', {
                            name: fileLabel,
                            defaultValue: `Show segments for ${fileLabel}`,
                          })
                      : t('race.toggleSegments.noSegments', {
                          name: fileLabel,
                          defaultValue: `No segments available for ${fileLabel}`,
                        });
                    return (
                      <div className="file-item" key={file.id}>
                        <div
                          className="file-item-header"
                          style={{ display: 'flex', alignItems: 'center' }}
                        >
                          {/* Botón siempre presente, solo muestra el SVG si hay segmentos */}
                          <button
                            className="toggle-segments-button"
                            onClick={() => {
                              if (file.segments && file.segments.length > 0) {
                                toggleFileExpansion(file.id);
                              }
                            }}
                            aria-label={toggleSegmentsLabel}
                            aria-expanded={hasSegments ? isExpanded : undefined}
                            title={toggleSegmentsLabel}
                            disabled={!hasSegments}
                          >
                            {file.segments && file.segments.length > 0 ? (
                              isExpanded ? (
                                <MinusIcon />
                              ) : (
                                <PlusIcon />
                              )
                            ) : null}
                          </button>

                          <div
                            className={`file-item-button-group ${fileButtonGroupClass} ${
                              isActiveFile
                                ? SIDEBAR_ACTIVE_GROUP_CLASS
                                : isActiveFileParent
                                  ? SIDEBAR_ACTIVE_PARENT_GROUP_CLASS
                                  : ''
                            }`}
                          >
                            <button
                              className={`file-item-button ${fileButtonClass} ${
                                isActiveFile ? SIDEBAR_ACTIVE_BUTTON_CLASS : ''
                              }`}
                              onClick={() => {
                                if (editingSegmentId !== null || editingFileId !== null) {
                                  return;
                                }
                                void handleFileClickAsync(file.id);
                              }}
                              onDoubleClick={() => {
                                if (!requireOwnership(file.sharing)) {
                                  return;
                                }
                                setEditingFileId(file.id);
                                setEditingFileText(file.name ?? '');
                              }}
                              disabled={editingSegmentId !== null || editingFileId !== null}
                              aria-current={isActiveFile ? 'true' : undefined}
                            >
                              <div className="file-item-button-content">
                                {editingFileId === file.id ? (
                                  <input
                                    type="text"
                                    className="race-name-input"
                                    value={editingFileText}
                                    onChange={(event) => setEditingFileText(event.target.value)}
                                    onBlur={() => void handleRenameFile(file.id)}
                                    onKeyDown={(event) => {
                                      if (event.key === 'Enter') {
                                        void handleRenameFile(file.id);
                                      }
                                      if (event.key === 'Escape') {
                                        setEditingFileId(null);
                                        setEditingFileText('');
                                      }
                                    }}
                                    ref={fileNameInputRef}
                                    style={{
                                      fontSize: 14,
                                      padding: '2px 6px',
                                    }}
                                  />
                                ) : (
                                  <span className="segment-item-label" title={file.name ?? ''}>
                                    {file.name}
                                  </span>
                                )}
                              </div>
                            </button>
                            {editingFileId !== file.id && canManageFile && (
                              <span className="file-item-menu">
                                <ThreeDotsMenu
                                  ariaLabel={t('files.options', 'File options')}
                                  items={[
                                    {
                                      label: renameLabel,
                                      icon: <IconEdit size={16} />,
                                      onClick: () => {
                                        setEditingFileId(file.id);
                                        setEditingFileText(file.name ?? '');
                                      },
                                    },
                                    {
                                      label: t(KEY_REPORTS_GENERATE, LBL_GENERATE_REPORT),
                                      icon: GENERATE_REPORT_ICON,
                                      onClick: () => {
                                        void handleGenerateReport(file.id);
                                      },
                                    },
                                    {
                                      label: deleteLabel,
                                      icon: <IconTrash size={16} />,
                                      onClick: () => {
                                        setDeleteConfirmation({
                                          type: 'file',
                                          id: file.id,
                                          label: file.name,
                                        });
                                      },
                                      color: 'red',
                                    },
                                  ]}
                                />
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Lista de segmentos: solo si hay segmentos y está expandido o modo global */}
                        {(expandedFiles[file.id] || showAllSegments) &&
                          file.segments &&
                          file.segments.length > 0 && (
                            <div className="segments-list">
                              {/* ► Races */}
                              {file.segments.some((s) => s.segment_type) && (
                                <>
                                  <h3>{t('reports.races')}</h3>
                                  {file.segments
                                    .filter((s) => s.segment_type)
                                    .map((race, rIndex) => {
                                      const raceDisplayName =
                                        race.name && race.name.trim().length > 0
                                          ? race.name
                                          : t(RACE_NUMBER_KEY, { n: rIndex + 1 });
                                      const legOptions = buildLegOptionsForSegment(
                                        race,
                                        file.modes,
                                      );
                                      const legsExpanded = expandedSegmentLegs[race.id] ?? false;
                                      const isActiveRace = isSidebarSegmentActive(file.id, race.id);
                                      const isActiveRaceParent = isSidebarSegmentActiveParent(
                                        file.id,
                                        race.id,
                                      );
                                      const raceActiveGroupClass = isActiveRace
                                        ? SIDEBAR_ACTIVE_GROUP_CLASS
                                        : isActiveRaceParent
                                          ? SIDEBAR_ACTIVE_PARENT_GROUP_CLASS
                                          : '';
                                      return (
                                        <div
                                          key={race.id}
                                          className="segment-item"
                                          style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                          }}
                                        >
                                          <div className="segment-row">
                                            {legOptions.length > 0 && (
                                              <button
                                                type="button"
                                                className="toggle-segments-button segment-leg-toggle"
                                                aria-label={t('race.toggleLegs', 'Mostrar tramos')}
                                                onClick={(event) => {
                                                  event.stopPropagation();
                                                  setExpandedSegmentLegs((previous) => ({
                                                    ...previous,
                                                    [race.id]: !legsExpanded,
                                                  }));
                                                }}
                                                disabled={editingSegmentId !== null}
                                                aria-expanded={legsExpanded}
                                              >
                                                {legsExpanded ? <MinusIcon /> : <PlusIcon />}
                                              </button>
                                            )}
                                            <div
                                              className={`file-item-button-group ${fileButtonGroupClass} ${raceActiveGroupClass}`}
                                            >
                                              <button
                                                onClick={() => {
                                                  if (editingSegmentId !== null) {
                                                    return;
                                                  }
                                                  void handleFileClickAsync(file.id, race.id);
                                                }}
                                                className={`segment-item-button ${fileButtonClass} ${
                                                  isActiveRace
                                                    ? SIDEBAR_ACTIVE_SEGMENT_BUTTON_CLASS
                                                    : ''
                                                }`}
                                                disabled={editingSegmentId !== null}
                                                aria-current={isActiveRace ? 'true' : undefined}
                                              >
                                                <div className="file-item-button-content">
                                                  {editingSegmentId === race.id ? (
                                                    <input
                                                      type="text"
                                                      className="race-name-input"
                                                      value={editingSegmentText}
                                                      onChange={(event) =>
                                                        setEditingSegmentText(event.target.value)
                                                      }
                                                      onBlur={() => handleRenameSegment(race.id)}
                                                      onKeyDown={(event) => {
                                                        if (event.key === 'Enter') {
                                                          void handleRenameSegment(race.id);
                                                        }
                                                        if (event.key === 'Escape') {
                                                          setEditingSegmentId(null);
                                                          setEditingSegmentText('');
                                                        }
                                                      }}
                                                      style={{
                                                        fontSize: 14,
                                                        padding: '2px 6px',
                                                      }}
                                                    />
                                                  ) : (
                                                    <span
                                                      className="segment-item-label"
                                                      title={raceDisplayName}
                                                    >
                                                      {raceDisplayName}
                                                    </span>
                                                  )}
                                                </div>
                                              </button>
                                              {editingSegmentId !== race.id && canManageFile && (
                                                <span className="file-item-menu">
                                                  <ThreeDotsMenu
                                                    ariaLabel={t('race.options', 'Race options')}
                                                    items={[
                                                      {
                                                        label: renameLabel,
                                                        icon: <IconEdit size={16} />,
                                                        onClick: () => {
                                                          setEditingSegmentId(race.id);
                                                          setEditingSegmentText(
                                                            (race.name ?? '').trim(),
                                                          );
                                                        },
                                                      },
                                                      {
                                                        label: t(
                                                          'race.segmentEditor.action',
                                                          'Edit segment',
                                                        ),
                                                        icon: <IconPencil size={16} />,
                                                        onClick: () => {
                                                          void startSegmentEditor(file, race);
                                                        },
                                                      },
                                                      {
                                                        label: t(
                                                          KEY_REPORTS_GENERATE,
                                                          LBL_GENERATE_REPORT,
                                                        ),
                                                        icon: GENERATE_REPORT_ICON,
                                                        onClick: () => {
                                                          void handleGenerateReport(
                                                            file.id,
                                                            race.id,
                                                          );
                                                        },
                                                      },
                                                      {
                                                        label: deleteLabel,
                                                        icon: <IconTrash size={16} />,
                                                        onClick: () => {
                                                          setDeleteConfirmation({
                                                            type: 'segment',
                                                            id: race.id,
                                                            label: raceDisplayName,
                                                          });
                                                        },
                                                        color: 'red',
                                                      },
                                                    ]}
                                                  />
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                          {legsExpanded && legOptions.length > 0 ? (
                                            <div className="segment-leg-list">
                                              {legOptions.map((leg) => {
                                                const isActiveLeg = isSidebarLegActive(
                                                  file.id,
                                                  race.id,
                                                  leg,
                                                );
                                                return (
                                                  <button
                                                    key={leg.id}
                                                    type="button"
                                                    className={`segment-leg-button ${
                                                      isActiveLeg ? SIDEBAR_ACTIVE_LEG_CLASS : ''
                                                    }`}
                                                    onClick={(event) => {
                                                      event.stopPropagation();
                                                      void handleFileClickAsync(file.id, race.id, {
                                                        start: leg.start,
                                                        end: leg.end,
                                                        kind: leg.kind,
                                                      });
                                                    }}
                                                    disabled={editingSegmentId !== null}
                                                    aria-current={isActiveLeg ? 'true' : undefined}
                                                  >
                                                    {leg.label}
                                                  </button>
                                                );
                                              })}
                                            </div>
                                          ) : null}
                                        </div>
                                      );
                                    })}
                                </>
                              )}

                              {/* ► Trainings */}
                              {file.segments.some((s) => !s.segment_type) && (
                                <>
                                  <h3>{t('training.title', 'Trainings')}:</h3>
                                  {file.segments
                                    .filter((s) => !s.segment_type)
                                    .map((training) => {
                                      const trainingDisplayName =
                                        training.name || `Training ${training.id}`;
                                      const legOptions = buildLegOptionsForSegment(
                                        training,
                                        file.modes,
                                      );
                                      const legsExpanded =
                                        expandedSegmentLegs[training.id] ?? false;
                                      const isActiveTraining = isSidebarSegmentActive(
                                        file.id,
                                        training.id,
                                      );
                                      const isActiveTrainingParent = isSidebarSegmentActiveParent(
                                        file.id,
                                        training.id,
                                      );
                                      const trainingActiveGroupClass = isActiveTraining
                                        ? SIDEBAR_ACTIVE_GROUP_CLASS
                                        : isActiveTrainingParent
                                          ? SIDEBAR_ACTIVE_PARENT_GROUP_CLASS
                                          : '';
                                      return (
                                        <div
                                          key={training.id}
                                          className="segment-item"
                                          style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                          }}
                                        >
                                          <div className="segment-row">
                                            {legOptions.length > 1 && (
                                              <button
                                                type="button"
                                                className="toggle-segments-button segment-leg-toggle"
                                                aria-label={t('race.toggleLegs', 'Mostrar tramos')}
                                                onClick={(event) => {
                                                  event.stopPropagation();
                                                  setExpandedSegmentLegs((previous) => ({
                                                    ...previous,
                                                    [training.id]: !legsExpanded,
                                                  }));
                                                }}
                                                disabled={editingSegmentId !== null}
                                                aria-expanded={legsExpanded}
                                              >
                                                {legsExpanded ? <MinusIcon /> : <PlusIcon />}
                                              </button>
                                            )}
                                            <div
                                              className={`file-item-button-group ${fileButtonGroupClass} ${trainingActiveGroupClass}`}
                                            >
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  if (editingSegmentId !== null) {
                                                    return;
                                                  }
                                                  void handleFileClickAsync(file.id, training.id);
                                                }}
                                                className={`segment-item-button ${fileButtonClass} ${
                                                  isActiveTraining
                                                    ? SIDEBAR_ACTIVE_SEGMENT_BUTTON_CLASS
                                                    : ''
                                                }`}
                                                disabled={editingSegmentId !== null}
                                                aria-current={
                                                  isActiveTraining ? 'true' : undefined
                                                }
                                              >
                                                <div className="file-item-button-content">
                                                  {editingSegmentId === training.id ? (
                                                    <input
                                                      type="text"
                                                      className="race-name-input"
                                                      value={editingSegmentText}
                                                      onChange={(event) =>
                                                        setEditingSegmentText(event.target.value)
                                                      }
                                                      onBlur={() =>
                                                        handleRenameSegment(training.id)
                                                      }
                                                      onKeyDown={(event) => {
                                                        if (event.key === 'Enter') {
                                                          void handleRenameSegment(training.id);
                                                        }
                                                        if (event.key === 'Escape') {
                                                          setEditingSegmentId(null);
                                                          setEditingSegmentText('');
                                                        }
                                                      }}
                                                      style={{
                                                        fontSize: 14,
                                                        padding: '2px 6px',
                                                      }}
                                                    />
                                                  ) : (
                                                    <span
                                                      className="segment-item-label"
                                                      title={trainingDisplayName}
                                                    >
                                                      {trainingDisplayName}
                                                    </span>
                                                  )}
                                                </div>
                                              </button>
                                              {editingSegmentId !== training.id &&
                                                canManageFile && (
                                                  <span className="file-item-menu">
                                                    <ThreeDotsMenu
                                                      ariaLabel={t(
                                                        'training.options',
                                                        'Training options',
                                                      )}
                                                      items={[
                                                        {
                                                          label: renameLabel,
                                                          icon: <IconEdit size={16} />,
                                                          onClick: () => {
                                                            setEditingSegmentId(training.id);
                                                            setEditingSegmentText(
                                                              (training.name ?? '').trim(),
                                                            );
                                                          },
                                                        },
                                                        {
                                                          label: t(
                                                            'race.segmentEditor.action',
                                                            'Edit segment',
                                                          ),
                                                          icon: <IconPencil size={16} />,
                                                          onClick: () => {
                                                            void startSegmentEditor(file, training);
                                                          },
                                                        },

                                                        {
                                                          label: t(
                                                            KEY_REPORTS_GENERATE,
                                                            LBL_GENERATE_REPORT,
                                                          ),
                                                          icon: GENERATE_REPORT_ICON,
                                                          onClick: () => {
                                                            void handleGenerateReport(
                                                              file.id,
                                                              training.id,
                                                            );
                                                          },
                                                        },
                                                        {
                                                          label: deleteLabel,
                                                          icon: <IconTrash size={16} />,
                                                          onClick: () => {
                                                            setDeleteConfirmation({
                                                              type: 'segment',
                                                              id: training.id,
                                                              label: trainingDisplayName,
                                                            });
                                                          },
                                                          color: 'red',
                                                        },
                                                      ]}
                                                    />
                                                  </span>
                                                )}
                                            </div>
                                          </div>
                                          {legsExpanded && legOptions.length > 1 ? (
                                            <div className="segment-leg-list">
                                              {legOptions.map((leg) => {
                                                const isActiveLeg = isSidebarLegActive(
                                                  file.id,
                                                  training.id,
                                                  leg,
                                                );
                                                return (
                                                  <button
                                                    key={leg.id}
                                                    type="button"
                                                    className={`segment-leg-button ${
                                                      isActiveLeg ? SIDEBAR_ACTIVE_LEG_CLASS : ''
                                                    }`}
                                                    onClick={(event) => {
                                                      event.stopPropagation();
                                                          void handleFileClickAsync(
                                                            file.id,
                                                            training.id,
                                                            { start: leg.start, end: leg.end, kind: leg.kind },
                                                          );
                                                    }}
                                                    disabled={editingSegmentId !== null}
                                                    aria-current={isActiveLeg ? 'true' : undefined}
                                                  >
                                                    {leg.label}
                                                  </button>
                                                );
                                              })}
                                            </div>
                                          ) : null}
                                        </div>
                                      );
                                    })}
                                </>
                              )}
                            </div>
                          )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {SHOW_SEGMENT_LABELS && segmentEditor && (
            <PerformanceLabelPanel
              fileId={segmentEditorFile?.id ?? segmentEditor.fileId}
              segmentId={segmentEditor.segment.id}
              fileName={segmentEditorFile?.name ?? null}
              segmentName={segmentEditorSegmentName}
              disabled={!segmentEditorFile || !isPrimaryTrackReady}
            />
          )}

          {selectedFile && isPrimaryTrackReady && (
            <Modal
              opened={isRelatedTracksModalOpen}
              onClose={handleCloseRelatedTracksModal}
              title={t('race.relatedTracks.title', 'Other overlapping files')}
              radius="md"
              centered
              size="lg"
              overlayProps={{ blur: 6, opacity: 0.55, zIndex: 951 }}
              zIndex={952}
              closeButtonProps={{
                type: 'button',
                onPointerDownCapture: stopClosePropagation,
                onPointerUpCapture: stopClosePropagation,
                onMouseDownCapture: stopClosePropagation,
                onMouseUpCapture: stopClosePropagation,
                onClick: handleRelatedTracksCloseClick,
              }}
              classNames={{
                title: 'related-tracks-modal-title',
                content: 'related-tracks-modal',
                body: 'related-tracks-modal-body',
                header: 'related-tracks-modal-header',
                close: 'related-tracks-modal-close',
              }}
            >
              <div className="related-tracks-modal-content">
                <p className="related-tracks-card-subtitle">
                  {t(
                    'race.relatedTracks.subtitle',
                    'Select additional boats to render on the map.',
                  )}
                </p>
                {isLoadingRelatedTracks ? (
                  <div className="related-tracks-status">
                    {t('race.relatedTracks.loading', 'Searching for related files...')}
                  </div>
                ) : overlayOptionsForView.length === 0 ? (
                  <div className="related-tracks-empty">
                    {t('race.relatedTracks.empty', 'No overlapping files detected for this track.')}
                  </div>
                ) : (
                  <>
                    <div className="related-tracks-selector">
                      {overlayOptionsForView.map((option) => {
                        const isSelected = selectedOverlayKeys.includes(option.key);
                        return (
                          <button
                            key={option.key}
                            type="button"
                            className={`related-tracks-option ${isSelected ? 'selected' : ''}`}
                            onClick={() => toggleRelatedTrackSelection(option.key)}
                          >
                            <span className="related-tracks-option-name">{option.label}</span>
                            {isSelected && (
                              <span className="related-tracks-option-check" aria-hidden="true">
                                <IconCheck size={16} />
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                    {overlayLayers.length > 0 && (
                      <div className="related-tracks-legend-container">
                        <span className="related-tracks-legend-label">
                          {t('race.relatedTracks.legend', 'Legend')}
                        </span>
                        <div className="related-tracks-legend">
                          {overlayLayers.map((layer: OverlayTrackLayer) => (
                            <div key={layer.fileId} className="related-track-pill">
                              <span
                                className="related-track-color-dot"
                                style={{ backgroundColor: layer.color }}
                              />
                              <span className="related-track-pill-label">{layer.label}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </Modal>
          )}

          {shouldShowEmptyMessage && (
            <div className="empty-message">
              <h3>{t('race.emptyTitle', 'Empty')}</h3>
              <p>{t('race.emptySubtitle', 'Select any event or date')}</p>
            </div>
          )}
          <div className="quadrant-selector-container">
            <QuadrantModeSelector
              panelModes={{
                1: panel1Mode,
                2: panel2Mode,
                3: panel3Mode,
                4: panel4Mode,
              }}
              onModeChange={(index, mode) => {
                switch (index) {
                  case 1: {
                    setPanel1Mode(mode);
                    break;
                  }
                  case 2: {
                    setPanel2Mode(mode);
                    break;
                  }
                  case 3: {
                    setPanel3Mode(mode);
                    break;
                  }
                  case 4: {
                    setPanel4Mode(mode);
                    break;
                  }
                }
              }}
            />
          </div>
          <div className="sidebar-floating-buttons">
            {selectedFile && isPrimaryTrackReady && (
              <button
                type="button"
                className="overlay-plus-button"
                onClick={() => setIsRelatedTracksModalOpen(true)}
                aria-label={overlayButtonLabel}
                title={overlayButtonLabel}
                disabled={isLoadingRelatedTracks}
              >
                <MapControlIcon name="relatedFiles" />
              </button>
            )}
            <button
              type="button"
              className="upload-button-report"
              onClick={toggleModal}
              aria-label={t('modals.uploadFile', 'Upload file')}
              disabled={!canManageSharing()}
              title={
                canManageSharing()
                  ? t('modals.uploadFile', 'Upload file')
                  : t('sharing.onlyMineActions', 'Solo puedes modificar tus propios datos')
              }
            >
              <UploadFileIcon />
            </button>
          </div>
        </div>
      )}
      <div className={`main-container ${effectiveLayout}`}>{renderPanels()}</div>
      <ModalRaceInsights
        isVisible={isModalVisible}
        onClose={toggleModal}
        userId={''}
        onUploadSuccess={(eventIdSubido) => {
          // Si coincide con el evento actualmente seleccionado
          if (eventIdSubido === selectedEventId) {
            resetTrackDataCache();
            // vuelvo a pedir la lista de archivos de ese evento
            void handleEventChange(String(eventIdSubido));
          }
        }}
      />
      {showSaveTrainingModal && (
        <ModalSaveTraining
          isVisible={showSaveTrainingModal}
          onClose={() => {
            setShowSaveTrainingModal(false);
            setSelectedHighlightSegment(null);
          }}
          onSave={handleConfirmSaveHighlight}
        />
      )}
      <Modal
        opened={Boolean(deleteConfirmation)}
        onClose={() => setDeleteConfirmation(null)}
        title={
          deleteConfirmation?.type === 'file'
            ? t('common.deleteFileConfirmTitle', 'Delete file')
            : t('common.deleteSegmentConfirmTitle', 'Delete segment')
        }
        centered
        classNames={{
          content: 'delete-confirm-modal',
          header: 'delete-confirm-modal-header',
          title: 'delete-confirm-modal-title',
          body: 'delete-confirm-modal-body',
        }}
      >
        <div className="delete-confirm-content">
          <p className="delete-confirm-message">
            {deleteConfirmation?.type === 'file'
              ? t('common.deleteFileConfirmMessage', {
                  name: deleteConfirmation?.label ?? t('common.file', 'File'),
                  defaultValue: 'This will delete the file. Continue?',
                })
              : t('common.deleteSegmentConfirmMessage', {
                  name: deleteConfirmation?.label ?? t('race.segmentEditor.segment', 'Segment'),
                  defaultValue: 'This will delete the segment. Continue?',
                })}
          </p>
          <div className="delete-confirm-actions">
            <button
              type="button"
              className="saveTrainingBtn cancelBtn"
              onClick={() => setDeleteConfirmation(null)}
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="saveTrainingBtn dangerBtn"
              onClick={() => {
                void handleConfirmDelete();
              }}
            >
              {deleteLabel}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

interface QuadrantModeSelectorProperties {
  panelModes: { [key: number]: PanelMode };
  onModeChange: (panelIndex: number, mode: PanelMode) => void;
}

export const QuadrantModeSelector: React.FC<QuadrantModeSelectorProperties> = ({
  panelModes,
  onModeChange,
}) => {
  const { t } = useTranslation('common');
  const [openQuadrantLocal, setOpenQuadrantLocal] = useState<number | null>(null);
  const containerReference = useRef<HTMLDivElement>(null);

  // Cerrar al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (error: MouseEvent) => {
      if (
        containerReference.current &&
        !containerReference.current.contains(error.target as Node)
      ) {
        setOpenQuadrantLocal(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const MODE_OPTIONS: Array<{
    mode: PanelMode;
    buttonIcon: React.ReactElement | null;
    dropdownIcon: React.ReactElement | null;
    labelKey: string;
    fallback: string;
  }> = [
    {
      mode: 'map',
      buttonIcon: <QuadrantModeIcon mode="map" />,
      dropdownIcon: <QuadrantModeIcon mode="map" />,
      labelKey: 'race.modes.map',
      fallback: 'Map',
    },
    {
      mode: 'line',
      buttonIcon: <QuadrantModeIcon mode="line" />,
      dropdownIcon: <QuadrantModeIcon mode="line" />,
      labelKey: 'race.modes.line',
      fallback: 'Line Chart',
    },
    {
      mode: 'polar',
      buttonIcon: <QuadrantModeIcon mode="polar" />,
      dropdownIcon: <QuadrantModeIcon mode="polar" />,
      labelKey: 'race.modes.polar',
      fallback: 'Polar',
    },
    {
      mode: 'correlation',
      buttonIcon: <QuadrantModeIcon mode="correlation" />,
      dropdownIcon: <QuadrantModeIcon mode="correlation" />,
      labelKey: 'race.modes.correlation',
      fallback: 'Correlations',
    },
    {
      mode: 'scatter',
      buttonIcon: <QuadrantModeIcon mode="scatter" />,
      dropdownIcon: <QuadrantModeIcon mode="scatter" />,
      labelKey: 'race.modes.scatter',
      fallback: 'Scatter',
    },
    {
      mode: 'maneuvers',
      buttonIcon: <QuadrantModeIcon mode="maneuvers" />,
      dropdownIcon: <QuadrantModeIcon mode="maneuvers" />,
      labelKey: 'race.modes.maneuvers',
      fallback: 'Maneuvers',
    },
    {
      mode: 'lineup',
      buttonIcon: <QuadrantModeIcon mode="lineup" />,
      dropdownIcon: <QuadrantModeIcon mode="lineup" />,
      labelKey: 'race.modes.lineup',
      fallback: 'Statistics',
    },
    {
      mode: 'performanceBuckets',
      buttonIcon: <QuadrantModeIcon mode="performanceBuckets" />,
      dropdownIcon: <QuadrantModeIcon mode="performanceBuckets" />,
      labelKey: 'race.modes.performanceBuckets',
      fallback: 'Performance',
    },
    {
      mode: 'binTable',
      buttonIcon: <QuadrantModeIcon mode="binTable" />,
      dropdownIcon: <QuadrantModeIcon mode="binTable" />,
      labelKey: 'race.modes.binTable',
      fallback: 'Bin Table',
    },
    {
      mode: 'none',
      buttonIcon: <PlusIcon className="quadrant-add-icon" />,
      dropdownIcon: <QuadrantModeIcon mode="none" />,
      labelKey: 'race.modes.none',
      fallback: 'Hidden',
    },
  ];

  const modes: PanelMode[] = MODE_OPTIONS.map((option) => option.mode);

  const getModeButtonIcon = (mode: PanelMode) =>
    MODE_OPTIONS.find((option) => option.mode === mode)?.buttonIcon ?? null;

  const getModeDropdownIcon = (mode: PanelMode) =>
    MODE_OPTIONS.find((option) => option.mode === mode)?.dropdownIcon ?? null;

  const getModeLabel = (mode: PanelMode) => {
    const option = MODE_OPTIONS.find((entry) => entry.mode === mode);
    if (!option) {
      return t('race.modes.none', 'Hidden');
    }
    return t(option.labelKey as Parameters<typeof t>[0], { defaultValue: option.fallback });
  };

  const normalizeMode = (mode: PanelMode): PanelMode =>
    MODE_OPTIONS.some((option) => option.mode === mode) ? mode : 'none';

  return (
    <div className="quadrant-grid" ref={containerReference}>
      {[1, 2, 3, 4].map((index) => {
        const currentMode = normalizeMode(panelModes[index]);
        return (
          <div key={index} className={`quadrant ${openQuadrantLocal === index ? 'open' : ''}`}>
            {/* Botón principal */}
            <button
              className="mode-button"
              onClick={() => setOpenQuadrantLocal(openQuadrantLocal === index ? null : index)}
              aria-label={
                currentMode === 'none'
                  ? t('race.modes.add', { defaultValue: 'Add view' })
                  : getModeLabel(currentMode)
              }
            >
              {getModeButtonIcon(currentMode) ?? (
                <span className="mode-button-placeholder" aria-hidden="true" />
              )}
            </button>

            {/* Dropdown de selección */}
            <div className="mode-dropdown">
              {(() => {
                const orderedModes: PanelMode[] = [
                  currentMode,
                  ...modes.filter((mode) => mode !== currentMode),
                ];
                return (
                  <>
                    {orderedModes.map((mode) => {
                      const icon = getModeDropdownIcon(mode);
                      const label = getModeLabel(mode);
                      return (
                        <button
                          key={mode}
                          className="mode-item"
                          data-mode={mode}
                          title={label}
                          onClick={() => {
                            setOpenQuadrantLocal(null);
                            onModeChange(index, mode);
                          }}
                        >
                          <span className="mode-item-icon" aria-hidden="true">
                            {icon}
                          </span>
                          <span className="mode-item-label">{label}</span>
                        </button>
                      );
                    })}
                  </>
                );
              })()}
            </div>
          </div>
        );
      })}
    </div>
  );
};
